import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import styled, { css } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../context/ThemeContext';
import { useTimezone } from '../context/TimezoneContext';
import { useAuth } from '../context/AuthContext';
import { ThemedText, Button, MiniMap, MapLinksDropdown, Pagination, ActionMenu, ErrorState, ConfirmDialog, GenerateDemoDataDialog } from '../components';
import { formatSubmissionDate, formatDateOnly, formatDateInTimezone } from '../utils/dateUtils';
import { renderTableCell } from '../registry/renderers';
import { parseCoordinates, parseImageList } from '../registry/renderers/parsers';
import { WaveformAudioPlayer } from '../components/FormBuilder/WaveformAudioPlayer';
import { Layout } from '../components/Layout';
import { formSchemaService, formSubmissionService, getSubmissionStatusLabel, columnConfigurationService, workOrderService, getWorkOrderStatusLabel } from '../api';
import pdfTemplateService from '../api/services/pdfTemplateService';
import pdfReportService from '../api/services/pdfReportService';
import submissionAnalyticsService from '../api/services/submissionAnalyticsService';
import { AnalyticsPanel } from '../components/AnalyticsPanel';
import { FilterPanel } from '../components/FilterPanel';
import { StandaloneVizListLauncher } from '../components/standalone/StandaloneVizListLauncher';
import { usePagination } from '../hooks/usePagination';
import { useRole } from '../hooks/useRole';
import { useReadOnly } from '../hooks/useReadOnly';
import useOrganizationConfig from '../hooks/useOrganizationConfig';
import { convertFormSchemaDefinitionToFields } from '../utils/schemaConverter';
import { evaluateRules } from '../utils/ruleEvaluator';
import { useDragAndDrop } from '@formkit/drag-and-drop/react';
import { SubmissionStatusColors, WorkOrderStatusColors, WorkOrderPriorityColors, withOpacity } from '../constants/statusColors';
import { renderFormIcon, renderFieldTypeIcon } from '../utils/iconUtils';
import { BarChart3, FileText, Plus, Pencil, Save, Star, Lock, Unlock, Trash2, ClipboardList, X, Inbox, ArrowDown, ArrowRight, ArrowUp, Flame, Copy, Eye, EyeOff, MapPin, Settings, ArrowLeft, Loader, FileSpreadsheet, Filter, RotateCcw, PlusCircle, MinusCircle, Rows3, Rows4, ChevronRight, Download, Upload, Send, XCircle, Camera, PenLine, Mic, Sparkles, Image as ImageIcon } from 'lucide-react';
import {
  useReactTable,
  getCoreRowModel,
  flexRender,
} from '@tanstack/react-table';

// Fix for default marker icons in react-leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const PageContainer = styled.div`
  max-width: 100%;
  margin: 0 auto;
`;


const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${props => props.theme.colors.muted};
`;

const SubmissionsTable = styled.div`
  background-color: #ffffff;
  border-radius: ${props => props.$noTopBar ? '16px 16px 0 0' : '0'};
  border: 1px solid #e2e5e9;
  border-top: ${props => props.$noTopBar ? '1px solid #e2e5e9' : 'none'};
  border-bottom: none;
  overflow-x: auto;
  position: relative;
  box-shadow: 0 4px 24px rgba(35,37,98,0.06);
  ${props => props.$compact && css`font-size: 12px;`}
`;

const TopScrollBar = styled.div`
  overflow-x: auto;
  overflow-y: hidden;
  border: 1px solid #e2e5e9;
  border-bottom: none;
  border-radius: 16px 16px 0 0;
  /* show only the scrollbar track, no content height */
  height: 14px;
  &::-webkit-scrollbar { height: 8px; }
  &::-webkit-scrollbar-track { background: #d1d5db; border-radius: 4px; }
  &::-webkit-scrollbar-thumb { background: #9ca3af; border-radius: 4px; }
  &::-webkit-scrollbar-thumb:hover { background: #6b7280; }
`;



const Resizer = styled.div`
  position: absolute;
  right: 0;
  top: 0;
  height: 100%;
  width: 5px;
  background: rgba(0, 0, 0, 0.5);
  cursor: col-resize;
  user-select: none;
  touch-action: none;
  opacity: 0;

  &:hover,
  &.isResizing {
    opacity: 1;
  }
`;

const TableHeader = styled.div`
  display: flex;
  min-width: 100%;
  ${props => props.width ? css`width: ${props.width}px;` : ''}
  padding: 0;
  background-color: #d1d5db;
  border-bottom: none;
  font-weight: 600;
  border-radius: 0;
  ${props => props.$compact && css`font-size: 12px;`}

  position: sticky;
  top: 0;
  z-index: 20;

  @media (max-width: 768px) {
    display: none;
  }
`;

const HeaderCell = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  padding: ${props => props.$compact
    ? `${props.theme.spacing.xs} ${props.theme.spacing.sm}`
    : props.$narrow
      ? `12px 6px`
      : `12px ${props.theme.spacing.lg}`};
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #43474f;

  /* Sticky styling for first two columns */
  ${props => props.stickyLeft && css`
    position: sticky;
    left: ${props.stickyOffset || 0}px;
    background-color: #d1d5db;
    z-index: 30;
    box-shadow: 2px 0 5px -2px rgba(0,0,0,0.04);
  `}

  /* Sticky styling for actions column (right) */
  ${props => props.stickyRight && css`
    position: sticky;
    right: 0;
    background-color: #d1d5db;
    z-index: 30;
  `}

  /* Second-to-last column fills remaining space, pushing actions flush right */
  &:nth-last-child(2) {
    flex: 1;
    width: auto !important;
  }
`;

const TableRow = styled.div`
  display: flex;
  min-width: 100%;
  ${props => props.width ? css`width: ${props.width}px;` : ''}
  background-color: ${props => props.$deleted ? '#fff5f5' : '#ffffff'};
  border-top: 1px solid ${props => props.$deleted ? '#fde8e8' : '#f0f1f5'};
  cursor: pointer;
  transition: background-color 0.15s ease;

  &:hover {
    background-color: ${props => props.$deleted ? '#fdecea' : '#f7f8ff'};
  }
`;

const TableCell = styled.div`
  display: flex;
  align-items: center;
  padding: ${props => props.$compact
    ? `${props.theme.spacing.xs} ${props.theme.spacing.sm}`
    : props.$narrow
      ? `${props.theme.spacing.md} 6px`
      : `${props.theme.spacing.md} ${props.theme.spacing.lg}`};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  /* Sticky styling for first two columns */
  ${props => props.stickyLeft && css`
    position: sticky;
    left: ${props.stickyOffset || 0}px;
    background-color: inherit;
    z-index: 10;
    box-shadow: 2px 0 4px -2px rgba(0,0,0,0.04);
    &::after { content: ''; }
  `}

  /* Sticky styling for actions column (right) */
  ${props => props.stickyRight && css`
    position: sticky;
    right: 0;
    background-color: inherit;
    z-index: 10;
  `}

  /* Second-to-last column fills remaining space, pushing actions flush right */
  &:nth-last-child(2) {
    flex: 1;
    width: auto !important;
  }
`;

const StatusBadge = styled.span`
  padding: 4px ${props => props.theme.spacing.sm};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
  text-align: center;
  ${props => {
    const c = SubmissionStatusColors[props.status];
    if (c) return `background-color: ${withOpacity(c)}; color: ${c};`;
    const themeC = [0, 5].includes(props.status) ? props.theme.colors.muted
      : props.status === 1 ? props.theme.colors.tint : props.theme.colors.muted;
    return `background-color: ${themeC}20; color: ${themeC};`;
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xxl} ${props => props.theme.spacing.lg};
`;

const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: ${props => props.theme.spacing.lg};
  opacity: 0.5;
`;

const PageHero = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 24px;
  flex-wrap: wrap;
  gap: 16px;
`;

const HeroLeft = styled.div`
  flex: 1;
  min-width: 0;
`;

const Breadcrumb = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #43474f;
  margin-bottom: 10px;
  font-weight: 500;
`;

const BreadcrumbLink = styled.span`
  cursor: pointer;
  &:hover { color: #232562; }
`;

const FormPageTitle = styled.h1`
  font-family: 'Manrope', sans-serif;
  font-size: 1.875rem;
  font-weight: 800;
  color: #232562;
  margin: 0 0 6px 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
`;

const FormPageDescription = styled.p`
  color: #43474f;
  font-size: 0.875rem;
  margin: 0;
  max-width: 560px;
  line-height: 1.5;
`;

const HeroActions = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
  padding-top: 8px;
`;

const KPIStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 14px;
  margin-bottom: 20px;
`;

const KPICard = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg || '12px'};
  padding: 20px;
  border: 1px solid ${props => props.theme.colors.border};
  display: flex;
  align-items: center;
  gap: 14px;
`;

const KPIIconBox = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.$bg || props.theme.colors.tint + '18'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const KPILabel = styled.div`
  font-size: 12px;
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 2px;
`;

const KPIValue = styled.div`
  font-size: 22px;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  line-height: 1.2;
`;

const ControlsBar = styled.div`
  background: #ffffff;
  border-radius: 14px;
  padding: 16px 24px;
  margin-bottom: 14px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  box-shadow: 0 1px 4px rgba(35,37,98,0.07);
  flex-wrap: wrap;
`;

const ControlsLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const ControlsRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ControlsDivider = styled.div`
  width: 1px;
  height: 18px;
  background: #c3c6d0;
  opacity: 0.6;
`;

const ViewingInfo = styled.span`
  font-size: 12px;
  color: #43474f;
  font-weight: 500;
  white-space: nowrap;
`;

const TableFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 20px;
  background: #d1d5db;
  border: 1px solid #e2e5e9;
  border-top: none;
  border-radius: 0 0 16px 16px;
  width: 100%;
`;

const FooterInfo = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: #43474f;
`;

const FooterNav = styled.div`
  display: flex;
  gap: 8px;
`;

const FooterNavBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 12px;
  font-weight: 700;
  color: #232562;
  padding: 4px 10px;
  border-radius: 6px;
  transition: background 0.15s;
  &:hover { background: #dde1ff; }
  &:disabled { opacity: 0.4; cursor: not-allowed; &:hover { background: none; } }
`;

const CardListBody = styled.div`
  margin: 10px;
  padding: 10px;
  background-color: #eaecf8;
  border-radius: 12px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CardItem = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.backgroundSecondary || '#ffffff'};
  border-radius: 10px;
  cursor: pointer;
  opacity: ${props => props.$deleted ? 0.55 : 1};
  transition: box-shadow 0.15s ease, transform 0.15s ease;
  &:hover {
    box-shadow: 0 2px 8px rgba(35,37,98,0.1);
    transform: translateY(-1px);
  }
`;

const CardRowLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
`;

const CardIconBox = styled.div`
  width: 30px;
  height: 30px;
  border-radius: 8px;
  background-color: ${props => props.theme.colors.tint + '18'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CardInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CardTitle = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const CardMeta = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const CardRowRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CardStatusBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  color: white;
  background-color: ${props => props.$color || '#6b7280'};
  white-space: nowrap;
`;

const CardIndexNum = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${props => props.theme.colors.muted};
  min-width: 20px;
  flex-shrink: 0;
`;

const SubmitterCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const SubmitterAvatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
  background: ${props => props.$bg};
  color: ${props => props.$color};
`;

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const DateLine = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.text};
`;

const TimeLine = styled.span`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
`;

const ShowDeletedToggle = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  color: ${props => props.theme.colors.muted};
  user-select: none;
`;

const ToggleTrack = styled.div`
  position: relative;
  width: 36px;
  height: 20px;
  border-radius: 10px;
  background: ${props => props.$on ? '#232562' : '#c3c6d0'};
  transition: background 0.2s ease;
  flex-shrink: 0;
`;

const ToggleThumb = styled.div`
  position: absolute;
  top: 3px;
  left: ${props => props.$on ? '19px' : '3px'};
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: #ffffff;
  transition: left 0.2s ease;
  box-shadow: 0 1px 3px rgba(0,0,0,0.2);
`;

const ColumnSelector = styled.div`
  position: relative;
  display: inline-block;
`;

const ColumnButton = styled(Button)`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
`;

const ColumnPanel = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  min-width: 300px;
  box-shadow: ${props => props.theme.shadows?.xl || '0 20px 25px -5px rgba(0, 0, 0, 0.1)'};
  z-index: 1000;
  max-height: 400px;
  overflow-y: auto;
`;

const ColumnOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: 8px 12px;
  cursor: ${props => props.draggable ? 'grab' : 'pointer'};
  border-radius: ${props => props.theme.borderRadius.md};
  transition: all 0.2s ease;
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  margin-bottom: ${props => props.theme.spacing.xs};

  &:hover {
    background-color: ${props => props.theme.colors.background};
    border-color: ${props => props.theme.colors.tint};
    transform: translateY(-1px);
    box-shadow: ${props => props.theme.shadows?.sm || '0 2px 4px rgba(0,0,0,0.05)'};
  }
  
  &:active {
    cursor: ${props => props.draggable ? 'grabbing' : 'pointer'};
    transform: translateY(0);
  }
`;

const Checkbox = styled.input.attrs({ type: 'checkbox' })`
  cursor: pointer;
  width: 16px;
  height: 16px;
  accent-color: ${props => props.theme.colors.tint};
`;

const DragHandle = styled.div`
  color: ${props => props.theme.colors.muted};
  cursor: grab;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 4px;
  border-radius: ${props => props.theme.borderRadius.sm};
  
  &:hover {
    color: ${props => props.theme.colors.text};
    background-color: ${props => props.theme.colors.background};
  }
  
  svg {
    width: 20px;
    height: 20px;
  }
`;

const LocationButton = styled(Button)`
  padding: 4px 12px;
  font-size: ${props => props.theme.typography.sizes.small};
  min-width: auto;
`;

const MediaPill = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 3px 10px;
  font-size: 12px;
  font-weight: 500;
  color: ${props => props.theme.colors.primary};
  background: ${props => props.theme.colors.backgroundAlt || props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  cursor: pointer;
  white-space: nowrap;
  transition: all 0.15s ease;

  &:hover {
    background: ${props => props.theme.colors.primary}12;
    border-color: ${props => props.theme.colors.primary}40;
  }
`;

const WorkOrderButton = styled(Button)`
  padding: 4px 12px;
  font-size: ${props => props.theme.typography.sizes.small};
  min-width: auto;
`;

const WOStatusBadge = styled.span`
  padding: 3px 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  white-space: nowrap;
  ${props => {
    const c = WorkOrderStatusColors[props.status] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const WOPriorityBadge = styled.span`
  padding: 3px 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  ${props => {
    const c = WorkOrderPriorityColors[props.priority] || props.theme.colors.muted;
    return `background-color: ${withOpacity(c)}; color: ${c};`;
  }}
`;

const WOModalBody = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const WOModalField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const WOModalFieldLabel = styled(ThemedText)`
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  font-weight: 600;
`;

const ConfigSelectorDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.sm};
  min-width: 320px;
  max-width: 400px;
  box-shadow: ${props => props.theme.shadows?.xl || '0 20px 25px -5px rgba(0, 0, 0, 0.1)'};
  z-index: 10001;
  max-height: 500px;
  overflow-y: auto;
`;

const ConfigOption = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  cursor: pointer;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: background-color 0.2s ease;
  border: 1px solid transparent;

  ${props => props.isActive && css`
    background-color: ${props.theme.colors.tint}20;
    border-color: ${props.theme.colors.tint};
  `}

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary};
  }
`;

const ConfigActions = styled.div`
  display: flex;
  gap: 4px;
  margin-left: auto;
  opacity: 0;
  transition: opacity 0.2s ease;

  ${ConfigOption}:hover & {
    opacity: 1;
  }
`;

const IconButton = styled.button`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: ${props => props.theme.colors.muted};
  font-size: 14px;
  border-radius: ${props => props.theme.borderRadius.sm};
  transition: all 0.2s ease;

  &:hover {
    background-color: ${props => props.theme.colors.background};
    color: ${props => props.theme.colors.text};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ConfigBadge = styled.span`
  padding: 2px 8px;
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: 11px;
  font-weight: 600;
  background-color: ${props => {
    if (props.type === 'default') return `${props.theme.colors.tint}20`;
    if (props.type === 'shared') return `${props.theme.colors.muted}20`;
    return 'transparent';
  }};
  color: ${props => {
    if (props.type === 'default') return props.theme.colors.tint;
    if (props.type === 'shared') return props.theme.colors.muted;
    return props.theme.colors.text;
  }};
`;

const ConfigSection = styled.div`
  padding: ${props => props.theme.spacing.xs} 0;

  &:not(:last-child) {
    border-bottom: 1px solid ${props => props.theme.colors.border};
    margin-bottom: ${props => props.theme.spacing.xs};
  }
`;

const ConfigSectionTitle = styled.div`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  color: ${props => props.theme.colors.muted};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const NewConfigInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.md};
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: 16px;
  padding: 28px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

// Inner list component that handles the actual drag and drop
const DraggableListInner = ({
  availableColumns,
  initialColumns,
  onReorder,
  toggleColumn,
  theme
}) => {
  const [columnListParent, reorderableColumns, setReorderableColumns] = useDragAndDrop(
    initialColumns,
    {
      group: 'columns',
      // dragHandle option removed to allow dragging the entire card
    }
  );

  // Track the last order we notified the parent about to prevent loops
  const lastNotifiedOrder = React.useRef(JSON.stringify(initialColumns));

  // Sync Parent -> Internal State (handles external reorders like Reset)
  // We only enable this for same-length updates (reorders).
  // Add/Remove is handled by the wrapper remounting us.
  useEffect(() => {
    // Check if order differs
    const initialStr = JSON.stringify(initialColumns);
    const currentStr = JSON.stringify(reorderableColumns);

    if (initialStr !== currentStr) {
      // Only update internal state if parent demands it AND it's different
      // But we must be careful not to overwrite a drag in progress?
      // Actually, if parent updates, we usually want to respect it (e.g. Reset).
      // But if parent updates BECAUSE we told it to, we shouldn't reset.

      // If the parent update matches what we last notified, we don't need to force set it.
      if (initialStr !== lastNotifiedOrder.current) {
        setReorderableColumns(initialColumns);
      }
    }
  }, [initialColumns, reorderableColumns, setReorderableColumns]);

  // Sync Internal State -> Parent (Drag)
  useEffect(() => {
    const currentStr = JSON.stringify(reorderableColumns);
    // Only notify if different from what we last sent/know
    // AND different from initial (to avoid notified on mount if same)
    if (currentStr !== lastNotifiedOrder.current) {
      lastNotifiedOrder.current = currentStr;
      onReorder(reorderableColumns);
    }
  }, [reorderableColumns, onReorder]);

  return (
    <div ref={columnListParent} style={{ marginTop: theme.spacing.sm }}>
      {reorderableColumns.map(colKey => {
        const column = availableColumns.find(c => c.key === colKey);

        // Fallback for missing columns (safety)
        if (!column) {
          return <div key={colKey} style={{ display: 'none' }}></div>;
        }

        return (
          <ColumnOption key={colKey} theme={theme} draggable>
            <DragHandle theme={theme}>
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M11 18c0 1.1-.9 2-2 2s-2-.9-2-2 .9-2 2-2 2 .9 2 2zm-2-8c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0-6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm6 4c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" />
              </svg>
            </DragHandle>
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, flex: 1, minWidth: 0 }}>
              <Checkbox
                theme={theme}
                checked={true}
                onChange={() => toggleColumn(colKey)}
              />
              <div style={{ color: theme.colors.muted, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                {renderFieldTypeIcon(column.fieldType, 14)}
              </div>
              <ThemedText type="default" style={{ fontWeight: 500, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{column.label}</ThemedText>
              <span style={{
                fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                background: theme.colors.hover || '#f0f0f0', color: theme.colors.muted,
                textTransform: 'uppercase', fontWeight: 500, flexShrink: 0
              }}>
                {column.fieldType || getColumnCategory(column)}
              </span>
            </div>
          </ColumnOption>
        );
      })}
    </div>
  );
};

// Wrapper component to isolate drag-and-drop context and force remount handling
const DraggableColumnList = ({
  availableColumns,
  frozenColumns,
  initialReorderableColumns,
  onReorder,
  toggleColumn,
  theme
}) => {
  // Compute a key that changes ONLY when the SET of columns changes (Add/Remove).
  // This forces the Inner component to remount, cleanly initializing useDragAndDrop with the new list.
  // We sort the keys so that order changes (Drag) DO NOT trigger remount.
  const structureKey = useMemo(() => {
    return [...initialReorderableColumns].sort().join(',');
  }, [initialReorderableColumns]);

  return (
    <DraggableListInner
      key={structureKey} // Force remount on structure change
      availableColumns={availableColumns}
      initialColumns={initialReorderableColumns}
      onReorder={onReorder}
      toggleColumn={toggleColumn}
      theme={theme}
    />
  );
};

const AVATAR_PALETTES = [
  { bg: '#dde1ff', color: '#232562' },
  { bg: '#d1fae5', color: '#065f46' },
  { bg: '#fef3c7', color: '#92400e' },
  { bg: '#fce7f3', color: '#9d174d' },
  { bg: '#e0e7ff', color: '#3730a3' },
  { bg: '#d1d5db', color: '#374151' },
];

const getAvatarPalette = (name) => {
  if (!name) return AVATAR_PALETTES[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
  return AVATAR_PALETTES[Math.abs(hash) % AVATAR_PALETTES.length];
};

const getInitials = (name) => {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

// Base columns configuration
const BASE_COLUMNS = [
  { key: 'select', label: '', default: true, type: 'base', fieldType: 'input' },
  { key: 'index', label: '#', default: true, type: 'base', fieldType: 'number' },
  { key: 'submitter', label: 'Submitter', default: true, type: 'base', fieldType: 'user' },
  { key: 'submitted', label: 'Submitted', default: true, type: 'base', fieldType: 'datetime' },
  { key: 'location', label: 'Location', default: true, type: 'base', fieldType: 'location' },
  { key: 'device', label: 'Device', default: false, type: 'base', fieldType: 'number' },
  { key: 'email', label: 'Email', default: false, type: 'base', fieldType: 'email' },
  { key: 'workOrder', label: 'Work Order', default: true, type: 'base', fieldType: 'input' },
  { key: 'workOrderStatus', label: 'WO Status', default: false, type: 'base', fieldType: 'select' },
  { key: 'workOrderPriority', label: 'WO Priority', default: false, type: 'base', fieldType: 'select' },
  { key: 'workOrderDueDate', label: 'WO Due Date', default: false, type: 'base', fieldType: 'date' },
  { key: 'workOrderAssignedTo', label: 'WO Assigned To', default: false, type: 'base', fieldType: 'user' },
  { key: 'workOrderCustomer', label: 'WO Customer', default: false, type: 'base', fieldType: 'user' },
];

// Column category classification
const WORK_ORDER_KEYS = new Set([
  'workOrder', 'workOrderStatus', 'workOrderPriority',
  'workOrderDueDate', 'workOrderAssignedTo', 'workOrderCustomer'
]);

const getColumnCategory = (column) => {
  if (column.isLabResult) return '🧪 Lab Results';
  if (column.type === 'schema') return 'Form Fields';
  if (WORK_ORDER_KEYS.has(column.key)) return 'Work Order';
  return 'System';
};

// Work order priority labels
const WO_PRIORITY_LABELS = { 0: 'Low', 1: 'Medium', 2: 'High', 3: 'Critical' };
const getWOPriorityIcon = (priority) => {
  switch (priority) {
    case 0: return <ArrowDown size={14} />;
    case 1: return <ArrowRight size={14} />;
    case 2: return <ArrowUp size={14} />;
    case 3: return <Flame size={14} />;
    default: return <ArrowRight size={14} />;
  }
};

export const FormSubmissionsPage = () => {
  const theme = useTheme();
  const { effectiveTimezone } = useTimezone();
  const navigate = useNavigate();
  const { formId } = useParams();
  const { enablePortalFormSubmission } = useOrganizationConfig();
  const { isAdmin } = useRole();
  const readOnly = useReadOnly();
  const { user } = useAuth();
  const [isDemoDataOpen, setIsDemoDataOpen] = useState(false);
  // "Generate Demo Data" is for demo-org builders only; read-only viewers would 403 on commit.
  const canGenerateDemoData = user?.isDemoOrg === true && !readOnly;
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [formData, setFormData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [unreadCommentCounts, setUnreadCommentCounts] = useState({});

  const {
    currentPage,
    totalCount,
    totalPages,
    pageSize,
    startIndex,
    endIndex,
    goToPage,
    resetPage,
    setTotalCount,
    paginationParams,
    extractPagedData,
  } = usePagination(20);

  // Schema fields state
  const [formFields, setFormFields] = useState([]);
  const [availableColumns, setAvailableColumns] = useState(BASE_COLUMNS);

  const [columnPanelOpen, setColumnPanelOpen] = useState(false);

  // Column configuration state
  const [availableConfigs, setAvailableConfigs] = useState([]); // All configs (user's + shared)
  const [currentConfig, setCurrentConfig] = useState(null); // Active configuration
  const [configLoading, setConfigLoading] = useState(true);
  const [configSaving, setConfigSaving] = useState(false);

  // Column UI state (derived from currentConfig)
  const [visibleColumns, setVisibleColumns] = useState([]);
  const [columnSizing, setColumnSizing] = useState({});

  // Column panel UI
  const [showConfigSelector, setShowConfigSelector] = useState(false);
  const [editingConfigName, setEditingConfigName] = useState(null);
  const [showSaveNameModal, setShowSaveNameModal] = useState(false);

  // Debounce timer for auto-save
  const autoSaveTimerRef = React.useRef(null);

  // Refs for syncing top and bottom horizontal scrollbars
  const tableScrollRef = useRef(null);
  const topScrollRef = useRef(null);
  const isSyncingScroll = useRef(false);

  const handleTableScroll = useCallback(() => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    if (topScrollRef.current && tableScrollRef.current) {
      topScrollRef.current.scrollLeft = tableScrollRef.current.scrollLeft;
    }
    isSyncingScroll.current = false;
  }, []);
  const handleTopScroll = useCallback(() => {
    if (isSyncingScroll.current) return;
    isSyncingScroll.current = true;
    if (tableScrollRef.current && topScrollRef.current) {
      tableScrollRef.current.scrollLeft = topScrollRef.current.scrollLeft;
    }
    isSyncingScroll.current = false;
  }, []);

  useEffect(() => {
    const el = tableScrollRef.current;
    if (!el) return;
    const check = () => setHasHorizontalScroll(el.scrollWidth > el.clientWidth);
    check();
    const observer = new ResizeObserver(check);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Frozen columns that cannot be moved or hidden
  const frozenColumns = ['select', 'index'];

  // Handle reorder from child component
  const handleColumnReorder = (newReorderableColumns) => {
    const newVisible = [...frozenColumns, ...newReorderableColumns];
    if (JSON.stringify(newVisible) !== JSON.stringify(visibleColumns)) {
      updateVisibleColumns(newVisible);
    }
  };

  // Location modal state
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Field media modal state
  const [fieldMediaModal, setFieldMediaModal] = useState({ open: false, type: null, data: null, loading: false });

  const openFieldMediaModal = async (submissionId, fieldId, mediaType) => {
    setFieldMediaModal({ open: true, type: mediaType, data: null, loading: true });
    try {
      const sub = await formSubmissionService.getSubmissionById(submissionId);
      const attachments = (sub.attachments || []).filter(a => a.fieldName === fieldId);
      setFieldMediaModal({ open: true, type: mediaType, data: { attachments, submissionId, fieldId }, loading: false });
    } catch {
      setFieldMediaModal({ open: false, type: null, data: null, loading: false });
    }
  };

  // Work order modal state
  const [workOrderModalOpen, setWorkOrderModalOpen] = useState(false);
  const [selectedWorkOrder, setSelectedWorkOrder] = useState(null);
  const [workOrderLoading, setWorkOrderLoading] = useState(false);

  // Selection state for CSV export
  const [selectedSubmissions, setSelectedSubmissions] = useState([]);

  // View density
  const [compactView, setCompactView] = useState(() => {
    try { return localStorage.getItem('aqrate_compact_view') === 'true'; } catch { return false; }
  });

  // Delete state
  const [deleteLoading, setDeleteLoading] = useState(false);

  // PDF generation state
  const [defaultTemplate, setDefaultTemplate] = useState(null);
  const [generatingPdfFor, setGeneratingPdfFor] = useState(null); // submission ID currently generating
  const [showInactive, setShowInactive] = useState(false);
  const [hasHorizontalScroll, setHasHorizontalScroll] = useState(false);

  // Analytics & field-level filtering state
  const [analyticsPanelOpen, setAnalyticsPanelOpen] = useState(false);
  const [aggregationData, setAggregationData] = useState(null);
  const [aggregationLoading, setAggregationLoading] = useState(false);
  const [fieldFilters, setFieldFilters] = useState([]);
  const [filterLogic, setFilterLogic] = useState('and');
  const [filterPanelOpen, setFilterPanelOpen] = useState(false);

  // Launch-time filter override for a ListView visualization opened from this page: the live filter state ONLY.
  // The launcher merges this onto the viz's SAVED config so the saved "Show top N" limit and column selections
  // are preserved (the render endpoint REPLACES the saved config with whatever override it receives). The backend
  // forces parent-only selection (no includeChildren) so repeated-form children never double-count as rows.
  const listVizFilterOverride = useMemo(
    () => ({ filters: fieldFilters, filterLogic, includeInactive: showInactive }),
    [fieldFilters, filterLogic, showInactive]
  );
  const [editFormWarningOpen, setEditFormWarningOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({ isOpen: false, title: '', message: '', confirmLabel: 'Confirm', onConfirm: null });
  const closeConfirm = () => setConfirmState(s => ({ ...s, isOpen: false }));

  const statusCounts = useMemo(() => {
    const counts = { submitted: 0, underReview: 0, approved: 0, rejected: 0 };
    submissions.forEach(s => {
      if (s.status === 'Submitted') counts.submitted++;
      else if (s.status === 'UnderReview') counts.underReview++;
      else if (s.status === 'Approved') counts.approved++;
      else if (s.status === 'Rejected') counts.rejected++;
    });
    return counts;
  }, [submissions]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setFetchError(null);

        // Fetch form details
        const form = await formSchemaService.getFormSchemaById(formId);
        setFormData(form);

        // Convert schema definition to fields (API v3.1+)
        let fields = [];
        if (form.schemaDefinition) {
          fields = convertFormSchemaDefinitionToFields(form.schemaDefinition);
        } else if (form.schemaJson) {
          // Backward compatibility
          try {
            const parsedSchema = JSON.parse(form.schemaJson);
            fields = convertFormSchemaDefinitionToFields(parsedSchema);
          } catch (error) {
            console.error('Failed to parse legacy schemaJson:', error);
          }
        }
        setFormFields(fields);

        // Create available columns from base + schema fields.
        // Lab-integration fields are flagged so the column picker groups them separately
        // and keeps them out of the default visible set — forms with 20+ analytes would
        // otherwise crowd the submissions list.
        const schemaColumns = fields.map(field => ({
          key: `field_${field.id}`,
          label: field.props.label + (field.props?.labResultSource?.unitHint ? ` (${field.props.labResultSource.unitHint})` : ''),
          default: false,
          type: 'schema',
          fieldId: field.id,
          fieldType: field.type,
          fieldOptions: field.props.options, // Store options for label lookup
          fieldPrecision: field.props?.precision,
          isLabResult: !!field.props?.labResultSource,
        }));

        setAvailableColumns([...BASE_COLUMNS, ...schemaColumns]);

        // Fetch submissions — use analytics filter endpoint when field filters are active.
        // includeChildren: true so submissions captured as children of another form's
        // repeated-form field still appear under their own form's listing.
        let submissionsData, total;
        if (fieldFilters.length > 0) {
          const filterResponse = await submissionAnalyticsService.filterSubmissions(formId, {
            filters: fieldFilters,
            filterLogic,
            ...paginationParams,
            includeInactive: showInactive,
            includeChildren: true,
          });
          submissionsData = filterResponse.items || filterResponse.$values || [];
          total = filterResponse.totalCount || 0;
        } else {
          const response = await formSubmissionService.getSubmissionsByFormId(formId, {
            ...paginationParams,
            includeInactive: showInactive,
            includeChildren: true,
          });
          ({ items: submissionsData, totalCount: total } = extractPagedData(response));
        }

        // When showInactive is true, fetch active-only IDs so we can identify which rows are deleted.
        // The list API does not include isActive in its DTO, so we diff the two sets.
        let activeIdSet = null;
        if (showInactive && submissionsData.length > 0) {
          try {
            const activeResp = await formSubmissionService.getSubmissionsByFormId(formId, {
              page: 1,
              pageSize: 2000,
              includeInactive: false,
              includeChildren: true,
            });
            const { items: activeItems } = extractPagedData(activeResp);
            activeIdSet = new Set(activeItems.map(s => s.id));
          } catch (_) {
            // If this secondary call fails, we just won't colour deleted rows
          }
        }

        // Map work order summary from API response (included in list endpoint)
        const mapped = submissionsData.map(s => {
          // A row is deleted if it's absent from the active-only set
          const _isDeleted = activeIdSet !== null ? !activeIdSet.has(s.id) : false;
          const wo = s.workOrderSummary;
          if (!wo) return { ...s, _isDeleted };
          return {
            ...s,
            _isDeleted,
            workOrderStatus: s.workOrderStatus || wo.status,
            workOrderPriority: s.workOrderPriority ?? wo.priority,
            workOrderDueDate: s.workOrderDueDate || wo.dueDate,
            workOrderAssignedToName: s.workOrderAssignedToName || wo.assignedToName,
            workOrderCustomerName: s.workOrderCustomerName || wo.customerName,
            workOrderTitle: s.workOrderTitle || wo.title,
          };
        });
        const sorted = [...mapped].sort((a, b) => {
          const dateA = new Date(a.submittedAt || a.createdAt || 0);
          const dateB = new Date(b.submittedAt || b.createdAt || 0);
          return dateB - dateA;
        });
        setSubmissions(sorted);
        setTotalCount(total);

        // Fetch unread comment counts (fire-and-forget, non-blocking)
        formSubmissionService.getUnreadCommentCounts()
          .then(counts => { if (counts && typeof counts === 'object') setUnreadCommentCounts(counts); })
          .catch(() => {});
      } catch (error) {
        console.error('Failed to fetch submissions:', error);
        setFetchError(error?.response?.data?.message || 'Failed to load submissions. Please check your connection and try again.');
        toast.error('Failed to load submissions');
      } finally {
        setLoading(false);
      }
    };

    if (formId) {
      fetchData();
    }
  }, [formId, currentPage, showInactive, fieldFilters]);

  // Fetch aggregation data when analytics panel is open
  useEffect(() => {
    if (!analyticsPanelOpen || !formId) return;

    const fetchAggregation = async () => {
      setAggregationLoading(true);
      try {
        const data = await submissionAnalyticsService.getAggregation(formId, {
          filters: fieldFilters.length > 0 ? fieldFilters : undefined,
          filterLogic: fieldFilters.length > 0 ? filterLogic : undefined,
          includeInactive: showInactive,
        });
        setAggregationData(data);
      } catch (err) {
        console.error('Failed to fetch aggregation:', err);
      } finally {
        setAggregationLoading(false);
      }
    };

    fetchAggregation();
  }, [analyticsPanelOpen, formId, fieldFilters, showInactive]);

  // Reset pagination when filters change
  const handleFieldFiltersChange = (newFilters, newLogic) => {
    setFieldFilters(newFilters);
    if (newLogic) setFilterLogic(newLogic);
    resetPage();
  };

  const removeFieldFilter = (index) => {
    setFieldFilters(prev => prev.filter((_, i) => i !== index));
    resetPage();
  };

  const clearAllFieldFilters = () => {
    setFieldFilters([]);
    resetPage();
  };

  const getFieldTitle = (fieldKey) => {
    const field = formFields.find(f => f.id === fieldKey);
    return field?.props?.label || field?.props?.name || fieldKey;
  };

  const formatFilterLabel = (filter) => {
    const { operator, value, valueTo, values } = filter;
    if (operator === 'isNull') return 'is empty';
    if (operator === 'isNotNull') return 'is not empty';
    if (operator === 'between') return `${value || '?'} – ${valueTo || '?'}`;
    if (operator === 'in' && values?.length) return values.join(', ');
    if (operator === 'contains') return `contains "${value}"`;
    if (operator === 'startsWith') return `starts with "${value}"`;
    if (operator === 'equals') return `= ${value}`;
    if (operator === 'gt') return `> ${value}`;
    if (operator === 'gte') return `>= ${value}`;
    if (operator === 'lt') return `< ${value}`;
    if (operator === 'lte') return `<= ${value}`;
    if (operator === 'before') return `before ${value}`;
    if (operator === 'after') return `after ${value}`;
    return value || '';
  };

  // Load default PDF template for Generate Report action
  useEffect(() => {
    if (!formId) return;
    const loadDefaultTemplate = async () => {
      try {
        const template = await pdfTemplateService.getDefaultTemplate(parseInt(formId));
        setDefaultTemplate(template);
      } catch {
        setDefaultTemplate(null);
      }
    };
    loadDefaultTemplate();
  }, [formId]);

  /**
   * Load configurations on mount and when formId changes
   */
  useEffect(() => {
    console.log('[ColumnConfig] useEffect triggered, formId:', formId);

    const loadConfigurations = async () => {
      if (!formId) {
        console.log('[ColumnConfig] No formId available, skipping load');
        return;
      }

      try {
        setConfigLoading(true);

        // Fetch all configs for this form
        const configs = await columnConfigurationService.getConfigsByFormId(formId);
        console.log('[ColumnConfig] Loaded configurations:', configs);
        console.log('[ColumnConfig] Current user ID:', getUserId());
        setAvailableConfigs(configs);

        // Try to load user's default config
        let activeConfig = null;
        try {
          activeConfig = await columnConfigurationService.getDefaultConfig(formId);
        } catch (error) {
          console.log('No default config found, will use system defaults');
        }

        if (activeConfig) {
          // Load from API config - fields are already deserialized
          setCurrentConfig(activeConfig);
          setVisibleColumns(activeConfig.visibleColumns || []);
          setColumnSizing(activeConfig.columnSizing || {});
        } else {
          // Fallback to system defaults
          const defaultVisible = BASE_COLUMNS.filter(col => col.default).map(col => col.key);
          setVisibleColumns(defaultVisible);
          setColumnSizing({});
          setCurrentConfig(null);
        }
      } catch (error) {
        console.error('[ColumnConfig] Failed to load column configurations:', error);
        console.error('[ColumnConfig] Error details:', {
          message: error.message,
          status: error.status,
          response: error.response
        });
        toast.error('Failed to load column configurations');

        // Fallback to defaults on error
        const defaultVisible = BASE_COLUMNS.filter(col => col.default).map(col => col.key);
        setVisibleColumns(defaultVisible);
        setColumnSizing({});
      } finally {
        setConfigLoading(false);
      }
    };

    loadConfigurations();
  }, [formId]);

  /**
   * Auto-save column changes to current configuration
   * Debounced to avoid excessive API calls
   */
  const autoSaveColumnChanges = React.useCallback((newVisible, newSizing) => {
    // Clear existing timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Only auto-save if we have an active config
    if (!currentConfig) {
      console.log('No active config to auto-save');
      return;
    }

    // Debounce save by 1 second
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        setConfigSaving(true);

        await columnConfigurationService.updateConfig(currentConfig.id, {
          visibleColumns: newVisible,
          columnSizing: newSizing
        });

        console.log('Column configuration auto-saved');
      } catch (error) {
        console.error('Failed to auto-save column configuration:', error);
        // Silent fail for auto-save

        // Handle specific errors
        if (error.status === 404) {
          toast.error('Configuration was deleted. Switching to defaults.');
          setCurrentConfig(null);
          const configs = await columnConfigurationService.getConfigsByFormId(formId);
          setAvailableConfigs(configs);
        } else if (error.status === 403) {
          toast.error('You no longer have permission to modify this configuration.');
          setCurrentConfig(null);
        }
      } finally {
        setConfigSaving(false);
      }
    }, 1000);
  }, [currentConfig, formId]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current);
      }
    };
  }, []);

  /**
   * Update visible columns with auto-save
   */
  const updateVisibleColumns = React.useCallback((newVisibleOrFn) => {
    setVisibleColumns(prev => {
      const newVal = typeof newVisibleOrFn === 'function'
        ? newVisibleOrFn(prev)
        : newVisibleOrFn;

      // Trigger auto-save (need to use current columnSizing)
      setColumnSizing(currentSizing => {
        autoSaveColumnChanges(newVal, currentSizing);
        return currentSizing;
      });

      return newVal;
    });
  }, [autoSaveColumnChanges]);

  /**
   * Update column sizing with auto-save
   */
  const updateColumnSizing = React.useCallback((newSizingOrFn) => {
    setColumnSizing(prev => {
      const newVal = typeof newSizingOrFn === 'function'
        ? newSizingOrFn(prev)
        : newSizingOrFn;

      // Trigger auto-save (need to use current visibleColumns)
      setVisibleColumns(currentVisible => {
        autoSaveColumnChanges(currentVisible, newVal);
        return currentVisible;
      });

      return newVal;
    });
  }, [autoSaveColumnChanges]);

  const handleSubmissionClick = (submission) => {
    navigate(`/forms/${formId}/submissions/${submission.id}`);
  };

  const toggleColumn = (columnKey) => {
    if (frozenColumns.includes(columnKey)) return;

    updateVisibleColumns(prev => {
      const currentReorderable = prev.filter(k => !frozenColumns.includes(k));
      const isVisible = prev.includes(columnKey);

      let newReorderable;
      if (isVisible) {
        newReorderable = currentReorderable.filter(k => k !== columnKey);
      } else {
        newReorderable = [...currentReorderable, columnKey];
      }

      return [...frozenColumns, ...newReorderable];
    });
  };

  const addColumnsByCategory = (category) => {
    updateVisibleColumns(prev => {
      const currentReorderable = prev.filter(k => !frozenColumns.includes(k));
      const toAdd = availableColumns
        .filter(c => !frozenColumns.includes(c.key) && !prev.includes(c.key) && getColumnCategory(c) === category)
        .map(c => c.key);
      return [...frozenColumns, ...currentReorderable, ...toAdd];
    });
  };

  const removeColumnsByCategory = (category) => {
    updateVisibleColumns(prev => {
      const keysToRemove = new Set(
        availableColumns
          .filter(c => getColumnCategory(c) === category)
          .map(c => c.key)
      );
      const currentReorderable = prev.filter(k => !frozenColumns.includes(k) && !keysToRemove.has(k));
      return [...frozenColumns, ...currentReorderable];
    });
  };

  /**
   * Get current user ID from localStorage
   */
  const getUserId = () => {
    try {
      const userData = JSON.parse(localStorage.getItem('userData'));
      return userData?.id;
    } catch {
      return null;
    }
  };

  /**
   * Create new configuration from current state
   */
  const handleCreateConfig = async (name) => {
    try {
      // Validate max configs
      const userConfigs = availableConfigs.filter(c => c.isOwner);
      if (userConfigs.length >= columnConfigurationService.MAX_CONFIGS_PER_USER) {
        toast.error(`Maximum ${columnConfigurationService.MAX_CONFIGS_PER_USER} configurations allowed`);
        return;
      }

      // Validate name
      const validation = columnConfigurationService.validateConfigName(name, availableConfigs);
      if (!validation.isValid) {
        toast.error(validation.errors[0]);
        return;
      }

      setConfigSaving(true);

      const newConfig = await columnConfigurationService.createConfig({
        name: name.trim(),
        formSchemaId: parseInt(formId),
        visibleColumns: visibleColumns,
        columnSizing: columnSizing,
        isDefault: false
      });

      setAvailableConfigs(prev => [...prev, newConfig]);
      toast.success(`Configuration "${name}" created`);
    } catch (error) {
      console.error('Failed to create configuration:', error);
      toast.error(error.message || 'Failed to create configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  const handleCreateDefaultConfig = async () => {
    try {
      setConfigSaving(true);
      const newConfig = await columnConfigurationService.createConfig({
        name: 'Default',
        formSchemaId: parseInt(formId),
        visibleColumns,
        columnSizing,
        isDefault: true
      });
      setAvailableConfigs(prev => [...prev, newConfig]);
      setCurrentConfig(newConfig);
      toast.success('Configuration saved as "Default"');
    } catch (error) {
      console.error('Failed to create default configuration:', error);
      toast.error(error.message || 'Failed to save configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  /**
   * Switch to different configuration
   */
  const handleSwitchConfig = async (configId) => {
    try {
      const config = availableConfigs.find(c => c.id === configId);
      if (!config) return;

      setCurrentConfig(config);
      setVisibleColumns(config.visibleColumns || []);
      setColumnSizing(config.columnSizing || {});
      setShowConfigSelector(false);

      toast.success(`Switched to "${config.name}"`);
    } catch (error) {
      console.error('Failed to switch configuration:', error);
      toast.error('Failed to switch configuration');
    }
  };

  /**
   * Set configuration as user's default
   */
  const handleSetDefault = async (configId) => {
    try {
      setConfigSaving(true);

      const updated = await columnConfigurationService.setAsDefault(configId);

      // Refresh list (backend may have created duplicate)
      const configs = await columnConfigurationService.getConfigsByFormId(formId);
      setAvailableConfigs(configs);

      // Apply new default - fields are already deserialized
      setCurrentConfig(updated);
      setVisibleColumns(updated.visibleColumns || []);
      setColumnSizing(updated.columnSizing || {});

      toast.success('Default configuration updated');
    } catch (error) {
      console.error('Failed to set default configuration:', error);
      toast.error(error.message || 'Failed to set default');
    } finally {
      setConfigSaving(false);
    }
  };

  /**
   * Rename configuration
   */
  const handleRenameConfig = async (configId, newName) => {
    try {
      const validation = columnConfigurationService.validateConfigName(
        newName,
        availableConfigs.filter(c => c.id !== configId)
      );
      if (!validation.isValid) {
        toast.error(validation.errors[0]);
        return;
      }

      setConfigSaving(true);

      const updated = await columnConfigurationService.updateConfig(configId, {
        name: newName.trim()
      });

      setAvailableConfigs(prev =>
        prev.map(c => c.id === configId ? updated : c)
      );

      if (currentConfig?.id === configId) {
        setCurrentConfig(updated);
      }

      toast.success('Configuration renamed');
      setEditingConfigName(null);
    } catch (error) {
      console.error('Failed to rename configuration:', error);
      toast.error(error.message || 'Failed to rename configuration');
    } finally {
      setConfigSaving(false);
    }
  };

  /**
   * Delete configuration
   */
  const handleDeleteConfig = async (configId) => {
    setConfirmState({
      isOpen: true,
      title: 'Delete Configuration',
      message: 'Delete this configuration? This cannot be undone.',
      confirmLabel: 'Delete',
      onConfirm: async () => {
        closeConfirm();
        try {
          setConfigSaving(true);

          await columnConfigurationService.deleteConfig(configId);

          setAvailableConfigs(prev => prev.filter(c => c.id !== configId));

          // If deleting current config, switch to defaults
          if (currentConfig?.id === configId) {
            const defaultVisible = BASE_COLUMNS.filter(col => col.default).map(col => col.key);
            setVisibleColumns(defaultVisible);
            setColumnSizing({});
            setCurrentConfig(null);
          }

          toast.success('Configuration deleted');
        } catch (error) {
          console.error('Failed to delete configuration:', error);
          toast.error(error.message || 'Failed to delete configuration');
        } finally {
          setConfigSaving(false);
        }
      },
    });
  };

  /**
   * Toggle share status
   */
  const handleToggleShare = async (configId) => {
    try {
      setConfigSaving(true);

      const updated = await columnConfigurationService.toggleShare(configId);

      setAvailableConfigs(prev =>
        prev.map(c => c.id === configId ? updated : c)
      );

      if (currentConfig?.id === configId) {
        setCurrentConfig(updated);
      }

      toast.success(updated.isShared ? 'Configuration shared' : 'Sharing disabled');
    } catch (error) {
      console.error('Failed to toggle share:', error);
      toast.error(error.message || 'Failed to update sharing');
    } finally {
      setConfigSaving(false);
    }
  };

  /**
   * Duplicate configuration
   */
  const handleDuplicateConfig = async (configId, originalName) => {
    try {
      // Generate new name
      let newName = `${originalName} (Copy)`;
      let counter = 1;
      while (availableConfigs.some(c => c.name === newName)) {
        counter++;
        newName = `${originalName} (Copy ${counter})`;
      }

      setConfigSaving(true);

      const duplicated = await columnConfigurationService.duplicateConfig(
        configId,
        newName
      );

      setAvailableConfigs(prev => [...prev, duplicated]);
      toast.success(`Configuration duplicated as "${newName}"`);
    } catch (error) {
      console.error('Failed to duplicate configuration:', error);
      toast.error(error.message || 'Failed to duplicate configuration');
    } finally {
      setConfigSaving(false);
    }
  };


  const showLocationModal = (submission) => {
    setSelectedLocation({
      latitude: submission.latitude,
      longitude: submission.longitude,
      name: submission.locationName || `${submission.latitude.toFixed(4)}, ${submission.longitude.toFixed(4)}`,
      submitter: submission.submitterName
    });
    setLocationModalOpen(true);
  };

  const showWorkOrderModal = async (submission) => {
    const woId = submission.workOrderId;
    if (!woId) return;

    // Set initial data from submission row (already enriched)
    setSelectedWorkOrder({
      id: woId,
      title: submission.workOrderTitle || `WO-${woId}`,
      status: submission.workOrderStatus,
      priority: submission.workOrderPriority,
      dueDate: submission.workOrderDueDate,
      assignedToName: submission.workOrderAssignedToName,
      customerName: submission.workOrderCustomerName,
    });
    setWorkOrderModalOpen(true);
    setWorkOrderLoading(true);

    try {
      const fullWO = await workOrderService.getWorkOrderById(woId);

      // WorkOrderResponseDto already includes resolved names
      setSelectedWorkOrder({
        ...fullWO,
        assignedToName: fullWO.assignedToUserName,
        customerName: fullWO.customerName,
      });
    } catch (error) {
      console.error('Failed to fetch work order details:', error);
    } finally {
      setWorkOrderLoading(false);
    }
  };

  // Selection handlers for CSV export
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedSubmissions(submissions.map(s => s.id));
    } else {
      setSelectedSubmissions([]);
    }
  };

  const handleSelectSubmission = (submissionId) => {
    setSelectedSubmissions(prev => {
      if (prev.includes(submissionId)) {
        return prev.filter(id => id !== submissionId);
      } else {
        return [...prev, submissionId];
      }
    });
  };

  const handleClearSelection = () => {
    setSelectedSubmissions([]);
  };

  /**
   * Handle bulk delete of selected submissions
   */
  const handleDeleteSelected = () => {
    if (selectedSubmissions.length === 0) return;

    // Confirmation dialog with count
    const confirmMessage = selectedSubmissions.length === 1
      ? 'Are you sure you want to delete this submission? It will be marked as inactive and can be restored later.'
      : `Are you sure you want to delete ${selectedSubmissions.length} submissions? They will be marked as inactive and can be restored later.`;

    setConfirmState({
      isOpen: true,
      title: 'Delete Submissions',
      message: confirmMessage,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        closeConfirm();
        try {
          setDeleteLoading(true);

          // Call bulk delete API
          await formSubmissionService.bulkDeleteSubmissions(selectedSubmissions);

          // Show success message
          toast.success(
            `${selectedSubmissions.length} submission${selectedSubmissions.length !== 1 ? 's' : ''} deleted successfully`
          );

          // Optimistic UI update: Remove deleted submissions from display
          setSubmissions(prev => prev.filter(s => !selectedSubmissions.includes(s.id)));
          setTotalCount(prev => prev - selectedSubmissions.length);

          // Clear selection
          setSelectedSubmissions([]);

        } catch (error) {
          console.error('Failed to delete submissions:', error);

          // Specific error handling
          if (error.status === 405) {
            toast.error('Delete endpoint not implemented on backend. Please implement DELETE /api/FormSubmission/bulk endpoint.');
          } else if (error.status === 403) {
            toast.error('You do not have permission to delete these submissions');
          } else if (error.status === 404) {
            toast.error('Some submissions were not found. They may have already been deleted.');
            // Refresh to sync state
            window.location.reload();
          } else if (error.status === 501 || error.status === 500) {
            toast.error('Backend endpoint not implemented. Please add soft delete functionality to the API.');
          } else if (error.message) {
            toast.error(error.message);
          } else {
            toast.error('Failed to delete submissions. Please try again.');
          }
        } finally {
          setDeleteLoading(false);
        }
      },
    });
  };

  /**
   * Generate a PDF report for a single submission using the default template.
   */
  const handleGeneratePdf = async (submissionId) => {
    if (!defaultTemplate) {
      toast.error('No default report template found. Please create one in Report Templates.');
      return;
    }

    try {
      setGeneratingPdfFor(submissionId);
      await pdfReportService.generateReport(submissionId, defaultTemplate.id);
      toast.success('PDF generation started. View the report in submission details.');
    } catch (error) {
      console.error('Failed to generate report:', error);
      toast.error('Failed to generate PDF report');
    } finally {
      setGeneratingPdfFor(null);
    }
  };

  /**
   * Delete a single submission (soft delete).
   */
  const handleDeleteSubmission = async (submissionId) => {
    // Ask the backend what deleting this submission would touch so the modal
    // can tell the user how many children would be orphaned vs shared with
    // other parents. Falls back to a plain confirm if the lookup fails.
    let impact = null;
    try {
      impact = await formSubmissionService.getDeleteImpact(submissionId);
    } catch {
      // Non-fatal — still allow deletion with a generic confirm.
    }

    const hasChildren = (impact?.childCount ?? 0) > 0;
    const baseMessage = 'Are you sure you want to delete this submission? It will be marked as inactive.';
    const childMessage = hasChildren
      ? `This submission has ${impact.childCount} child submission${impact.childCount === 1 ? '' : 's'}`
      + (impact.sharedCount > 0 ? ` (${impact.sharedCount} shared with other submissions)` : '')
      + `. ${impact.orphanCount > 0
          ? `${impact.orphanCount} will be orphaned and deleted; `
          : ''}`
      + (impact.sharedCount > 0 ? 'shared children will be unlinked but preserved.' : '')
      : '';

    setConfirmState({
      isOpen: true,
      title: 'Delete Submission',
      message: hasChildren ? `${baseMessage}\n\n${childMessage}` : baseMessage,
      confirmLabel: 'Delete',
      onConfirm: async () => {
        closeConfirm();
        try {
          // When the parent has orphan-eligible children, cascade their deletion
          // too so the user isn't left with dangling unreachable rows.
          const deleteOrphans = (impact?.orphanCount ?? 0) > 0;
          await formSubmissionService.deleteSubmission(submissionId, { deleteOrphans });
          toast.success('Submission deleted successfully');
          setSubmissions(prev => prev.filter(s => s.id !== submissionId));
          setTotalCount(prev => prev - 1);
          setSelectedSubmissions(prev => prev.filter(id => id !== submissionId));
        } catch (error) {
          console.error('Failed to delete submission:', error);
          toast.error('Failed to delete submission');
        }
      },
    });
  };

  // Get value for a schema field from submission data (API v3.1+)
  const getSchemaFieldValue = (submission, fieldId) => {
    try {
      const fieldValues = formSubmissionService.getSubmissionFields(submission.submissionData);
      return fieldValues[fieldId] || 'N/A';
    } catch (error) {
      return 'N/A';
    }
  };

  // Advanced Field Rules — canonical fields for the shared evaluator.
  const ruleCanonicalFields = useMemo(
    () => formFields.map(f => ({ key: f.id, options: f.props?.options, rules: f.props?.rules })),
    [formFields]
  );

  // True when any schema field carries a `format` rule — lets us skip the
  // per-row evaluation entirely on the common (rule-free) form.
  const hasFormatRules = useMemo(
    () => ruleCanonicalFields.some(f =>
      Array.isArray(f.rules) && f.rules.some(r => r && r.effect === 'format')),
    [ruleCanonicalFields]
  );

  // Per-row evaluated field styles, memoized by submission id so each row is
  // only evaluated once even as the table re-renders.
  const rowStylesCache = useRef(new Map());
  useEffect(() => { rowStylesCache.current = new Map(); }, [ruleCanonicalFields]);

  const getRowFieldStyles = useCallback((submission) => {
    if (!hasFormatRules || !submission) return null;
    const cache = rowStylesCache.current;
    if (cache.has(submission.id)) return cache.get(submission.id);
    let styles = null;
    try {
      const values = formSubmissionService.getSubmissionFields(submission.submissionData) || {};
      styles = evaluateRules(ruleCanonicalFields, values).fieldStyles || {};
    } catch (e) {
      styles = {};
    }
    cache.set(submission.id, styles);
    return styles;
  }, [hasFormatRules, ruleCanonicalFields]);

  // Render schema field value based on field type (registry-driven).
  // `fieldColor` applies an Advanced Field Rules `format` style when present.
  const renderSchemaFieldValue = (value, fieldType, fieldOptions = null, fieldPrecision = null, fieldColor = null) => {
    if (value === null || value === undefined || value === 'N/A') {
      return 'N/A';
    }
    return renderTableCell(
      { type: fieldType, props: { options: fieldOptions, precision: fieldPrecision } },
      value,
      { timezone: effectiveTimezone, options: fieldOptions, fieldColor }
    );
  };

  const formatDate = (dateString) => {
    return formatSubmissionDate(dateString, effectiveTimezone);
  };

  // --- TanStack Table Configuration ---
  const tableColumns = useMemo(() => {
    const cols = visibleColumns.map(colKey => {
      const colMeta = availableColumns.find(c => c.key === colKey);
      if (!colMeta) return null;

      const columnDef = {
        id: colKey,
        header: colMeta.label,
        size: colKey === 'select' ? 30 : colKey === 'index' ? 44 : 150,
        minSize: 50,
        enableResizing: colKey !== 'select',
      };

      if (colKey === 'select') {
        return {
          ...columnDef,
          enableResizing: false,
          size: 30,
          minSize: 30,
          header: () => (
            <div onClick={e => e.stopPropagation()} style={{ paddingLeft: 6 }}>
              <Checkbox
                theme={theme}
                checked={submissions.length > 0 && selectedSubmissions.length === submissions.length}
                onChange={handleSelectAll}
                title="Select all"
              />
            </div>
          ),
          cell: ({ row }) => (
            <div onClick={e => e.stopPropagation()} style={{ paddingLeft: 6 }}>
              <Checkbox
                theme={theme}
                checked={selectedSubmissions.includes(row.original.id)}
                onChange={() => handleSelectSubmission(row.original.id)}
                title="Select submission"
              />
            </div>
          ),
        };
      }

      if (colKey === 'index') {
        return {
          ...columnDef,
          size: 44,
          minSize: 44,
          header: () => <span style={{ width: '100%', textAlign: 'center', display: 'block' }}>#</span>,
          cell: ({ row }) => (
            <ThemedText type="defaultSemiBold" style={{ width: '100%', textAlign: 'center' }}>
              {(currentPage - 1) * 20 + row.index + 1}
            </ThemedText>
          ),
        };
      }

      // Base columns
      if (colMeta.type === 'base') {
        switch (colKey) {
          case 'submitter':
            return {
              ...columnDef,
              size: 200,
              cell: ({ row }) => {
                const name = row.original.submitterName || 'Anonymous';
                const palette = getAvatarPalette(name);
                return (
                  <SubmitterCell>
                    <SubmitterAvatar $bg={palette.bg} $color={palette.color}>{getInitials(name)}</SubmitterAvatar>
                    <ThemedText type="defaultSemiBold">{name}</ThemedText>
                  </SubmitterCell>
                );
              }
            };
          case 'email':
            return {
              ...columnDef,
              size: 200,
              cell: ({ row }) => (
                <ThemedText type="small" style={{ color: theme.colors.muted }}>{row.original.submitterEmail || 'N/A'}</ThemedText>
              )
            };
          case 'submitted':
            return {
              ...columnDef,
              size: 160,
              cell: ({ row }) => {
                const displayTime = row.original.isOfflineSubmission && row.original.deviceTimestamp && !row.original.submittedAt
                  ? row.original.deviceTimestamp
                  : (row.original.submittedAt || row.original.createdAt);
                const tz = effectiveTimezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
                const d = displayTime ? new Date(displayTime) : null;
                const datePart = d ? new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'short', day: 'numeric', timeZone: tz }).format(d) : 'N/A';
                const timePart = d ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', hour12: true, timeZone: tz }).format(d) : '';
                return (
                  <DateCell>
                    <DateLine theme={theme}>{datePart}</DateLine>
                    <TimeLine theme={theme}>{timePart}</TimeLine>
                  </DateCell>
                );
              }
            };
          case 'location':
            return {
              ...columnDef,
              size: 150,
              cell: ({ row }) => (
                <div onClick={e => e.stopPropagation()}>
                  {row.original.latitude && row.original.longitude ? (
                    <LocationButton
                      theme={theme}
                      variant="secondary"
                      size="sm"
                      onClick={() => showLocationModal(row.original)}
                      title="View Map"
                      style={{ padding: '4px 8px', minWidth: 'auto' }}
                    >
                      <MapPin size={14} />
                    </LocationButton>
                  ) : (
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>N/A</ThemedText>
                  )}
                </div>
              )
            };
          case 'device':
            return {
              ...columnDef,
              size: 150,
              cell: ({ row }) => (
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  {row.original.deviceId ? row.original.deviceId.substring(0, 15) + '...' : 'N/A'}
                </ThemedText>
              )
            };
          case 'workOrder':
            return {
              ...columnDef,
              size: 200,
              cell: ({ row }) => {
                const woId = row.original.workOrderId;
                if (!woId) {
                  return <ThemedText type="small" style={{ color: theme.colors.muted }}>-</ThemedText>;
                }
                return (
                  <div onClick={e => e.stopPropagation()}>
                    <WorkOrderButton
                      theme={theme}
                      variant="secondary"
                      size="sm"
                      onClick={() => showWorkOrderModal(row.original)}
                    >
                      <ClipboardList size={14} /> View WO
                    </WorkOrderButton>
                  </div>
                );
              }
            };
          case 'workOrderStatus':
            return {
              ...columnDef,
              size: 130,
              cell: ({ row }) => {
                const status = row.original.workOrderStatus;
                if (!row.original.workOrderId || !status) {
                  return <ThemedText type="small" style={{ color: theme.colors.muted }}>-</ThemedText>;
                }
                return <WOStatusBadge theme={theme} status={status}>{getWorkOrderStatusLabel(status)}</WOStatusBadge>;
              }
            };
          case 'workOrderPriority':
            return {
              ...columnDef,
              size: 130,
              cell: ({ row }) => {
                const priority = row.original.workOrderPriority;
                if (!row.original.workOrderId || priority === undefined || priority === null) {
                  return <ThemedText type="small" style={{ color: theme.colors.muted }}>-</ThemedText>;
                }
                return (
                  <WOPriorityBadge theme={theme} priority={priority}>
                    {getWOPriorityIcon(priority)} {WO_PRIORITY_LABELS[priority] || 'Medium'}
                  </WOPriorityBadge>
                );
              }
            };
          case 'workOrderDueDate':
            return {
              ...columnDef,
              size: 140,
              cell: ({ row }) => {
                const dueDate = row.original.workOrderDueDate;
                if (!row.original.workOrderId || !dueDate) {
                  return <ThemedText type="small" style={{ color: theme.colors.muted }}>-</ThemedText>;
                }
                return <ThemedText type="small">{formatDateOnly(dueDate)}</ThemedText>;
              }
            };
          case 'workOrderAssignedTo':
            return {
              ...columnDef,
              size: 160,
              cell: ({ row }) => {
                const name = row.original.workOrderAssignedToName;
                if (!row.original.workOrderId || !name) {
                  return <ThemedText type="small" style={{ color: theme.colors.muted }}>-</ThemedText>;
                }
                return <ThemedText type="small">{name}</ThemedText>;
              }
            };
          case 'workOrderCustomer':
            return {
              ...columnDef,
              size: 160,
              cell: ({ row }) => {
                const name = row.original.workOrderCustomerName;
                if (!row.original.workOrderId || !name) {
                  return <ThemedText type="small" style={{ color: theme.colors.muted }}>-</ThemedText>;
                }
                return <ThemedText type="small">{name}</ThemedText>;
              }
            };
        }
      }

      // Schema fields
      if (colMeta.type === 'schema') {
        const ft = colMeta.fieldType;

        // Media/location fields render as interactive icon pills
        if (ft === 'image_camera' || ft === 'drawing' || ft === 'Image' || ft === 'Drawing') {
          return {
            ...columnDef,
            size: 120,
            cell: ({ row }) => {
              const val = getSchemaFieldValue(row.original, colMeta.fieldId);
              if (!val || val === 'N/A') return <ThemedText type="small" style={{ color: theme.colors.muted }}>—</ThemedText>;
              const items = parseImageList(val);
              if (items.length === 0) return <ThemedText type="small" style={{ color: theme.colors.muted }}>—</ThemedText>;
              const Icon = (ft === 'drawing' || ft === 'Drawing') ? PenLine : Camera;
              const label = items.length === 1 ? '1 image' : `${items.length} images`;
              return (
                <div onClick={e => e.stopPropagation()}>
                  <MediaPill theme={theme} onClick={() => openFieldMediaModal(row.original.id, colMeta.fieldId, 'image')}>
                    <Icon size={13} /> {label}
                  </MediaPill>
                </div>
              );
            }
          };
        }

        if (ft === 'voice' || ft === 'Voice') {
          return {
            ...columnDef,
            size: 100,
            cell: ({ row }) => {
              const val = getSchemaFieldValue(row.original, colMeta.fieldId);
              if (!val || val === 'N/A') return <ThemedText type="small" style={{ color: theme.colors.muted }}>—</ThemedText>;
              return (
                <div onClick={e => e.stopPropagation()}>
                  <MediaPill theme={theme} onClick={() => openFieldMediaModal(row.original.id, colMeta.fieldId, 'audio')}>
                    <Mic size={13} /> Audio
                  </MediaPill>
                </div>
              );
            }
          };
        }

        if (ft === 'geolocation' || ft === 'location' || ft === 'Location') {
          return {
            ...columnDef,
            size: 120,
            cell: ({ row }) => {
              const val = getSchemaFieldValue(row.original, colMeta.fieldId);
              if (!val || val === 'N/A') return <ThemedText type="small" style={{ color: theme.colors.muted }}>—</ThemedText>;
              const coords = parseCoordinates(val);
              if (!coords) return <ThemedText type="small" style={{ color: theme.colors.muted }}>—</ThemedText>;
              return (
                <div onClick={e => e.stopPropagation()}>
                  <MediaPill theme={theme} onClick={() => {
                    setFieldMediaModal({
                      open: true,
                      type: 'location',
                      data: { ...coords, label: colMeta.label },
                    });
                  }}>
                    <MapPin size={13} /> Location
                  </MediaPill>
                </div>
              );
            }
          };
        }

        return {
          ...columnDef,
          size: 200,
          cell: ({ row }) => {
            const val = getSchemaFieldValue(row.original, colMeta.fieldId);
            const rowStyles = getRowFieldStyles(row.original);
            const fieldColor = rowStyles ? rowStyles[colMeta.fieldId] : null;
            const rendered = renderSchemaFieldValue(
              val, colMeta.fieldType, colMeta.fieldOptions, colMeta.fieldPrecision, fieldColor
            );
            return <ThemedText type="small" style={{ color: theme.colors.muted }}>{rendered}</ThemedText>;
          }
        };
      }

      return columnDef;
    }).filter(Boolean);

    // Always append actions column (non-optional, like select/index)
    cols.push({
      id: 'actions',
      header: '',
      size: compactView ? 50 : 70,
      minSize: compactView ? 50 : 70,
      enableResizing: false,
      cell: ({ row }) => (
        <div onClick={e => e.stopPropagation()}>
          <ActionMenu actions={[
            {
              label: generatingPdfFor === row.original.id ? 'Generating...' : 'Generate Report',
              onClick: () => handleGeneratePdf(row.original.id)
            },
            ...(isAdmin() && !readOnly ? [{
              label: 'Delete',
              onClick: () => handleDeleteSubmission(row.original.id),
              variant: 'danger'
            }] : [])
          ]} />
        </div>
      )
    });

    return cols;
  }, [visibleColumns, availableColumns, submissions, selectedSubmissions, currentPage, theme, generatingPdfFor, isAdmin]);

  const table = useReactTable({
    data: submissions,
    columns: tableColumns,
    state: {
      columnSizing,
    },
    onColumnSizingChange: updateColumnSizing,
    columnResizeMode: 'onChange',
    getCoreRowModel: getCoreRowModel(),
    enableColumnResizing: true,
  });

  const handleBackToForms = () => {
    navigate('/forms');
  };

  const handleCreateSubmission = () => {
    navigate(`/forms/${formId}/submissions/create`);
  };

  // CSV export handler
  const handleExportCSV = async () => {
    try {
      // Show info about what's being exported
      const exportCount = selectedSubmissions.length > 0 ? selectedSubmissions.length : totalCount;
      const inactiveNote = showInactive ? ' (including deleted)' : '';
      toast.info(`Preparing CSV export for ${exportCount} submission${exportCount !== 1 ? 's' : ''}${inactiveNote}...`);

      const exportParams = {
        formSchemaId: formId,
        includeInactive: showInactive,
      };

      // Add selected submission IDs if any are selected
      if (selectedSubmissions.length > 0) {
        exportParams.submissionIds = selectedSubmissions;
      }

      const blob = await formSubmissionService.exportToCSV(exportParams);

      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const suffix = selectedSubmissions.length > 0 ? `_selected_${selectedSubmissions.length}` : '';
      const inactiveSuffix = showInactive ? '_with_deleted' : '';
      link.download = `submissions_${formData?.name || 'form'}${suffix}${inactiveSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      toast.success('CSV exported successfully');
    } catch (error) {
      console.error('Failed to export CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  if (loading) {
    return (
      <Layout currentPath={`/forms/${formId}/submissions`}>
        <LoadingContainer theme={theme}>
          <ThemedText>Loading submissions...</ThemedText>
        </LoadingContainer>
      </Layout>
    );
  }

  if (fetchError && !formData) {
    return (
      <Layout currentPath={`/forms/${formId}/submissions`}>
        <ErrorState
          theme={theme}
          title="Failed to load submissions"
          message={fetchError}
          onRetry={() => window.location.reload()}
        />
      </Layout>
    );
  }

  return (
    <Layout currentPath={`/forms/${formId}/submissions`}>
      <Helmet>
        <title>{formData?.name || 'Form'} Submissions - aQRate Portal</title>
        <meta name="description" content={`View submissions for ${formData?.name}`} />
      </Helmet>

      <PageContainer theme={theme}>
        {/* Hero */}
        <PageHero>
          <HeroLeft>
            <Breadcrumb>
              <BreadcrumbLink onClick={() => navigate('/forms')}>My Forms</BreadcrumbLink>
              <ChevronRight size={12} />
              <span style={{ color: '#232562', fontWeight: 700 }}>{formData?.name || 'Form'}</span>
            </Breadcrumb>
            <FormPageTitle>{formData?.name || 'Form Submissions'}</FormPageTitle>
            <FormPageDescription>
              {formData?.description || 'View and manage all submissions for this form'}
            </FormPageDescription>
          </HeroLeft>
          <HeroActions>
            {selectedSubmissions.length > 0 && (
              <>
                <ThemedText type="defaultSemiBold" style={{ color: theme.colors.tint }}>
                  {selectedSubmissions.length} selected
                </ThemedText>
                {isAdmin() && (
                  <Button variant="danger" onClick={handleDeleteSelected} disabled={deleteLoading}>
                    {deleteLoading ? <><Loader size={14} /> Deleting...</> : <><Trash2 size={14} /> Delete Selected</>}
                  </Button>
                )}
                <Button variant="secondary" onClick={handleClearSelection}>
                  <X size={14} /> Clear Selection
                </Button>
              </>
            )}
            <Button
              variant="secondary"
              onClick={() => navigate(`/forms/${formId}/submissions/export-excel`, { state: { selectedSubmissions, showInactive, formData } })}
            >
              <Download size={14} /> Export
            </Button>
            {isAdmin() && (
              <Button
                variant="secondary"
                onClick={() => navigate(`/forms/${formId}/submissions/import-excel`, { state: { formData } })}
              >
                <Upload size={14} /> Import
              </Button>
            )}
            {canGenerateDemoData && (
              <Button
                variant="secondary"
                onClick={() => setIsDemoDataOpen(true)}
                title="Generate realistic demo submissions for this form"
              >
                <Sparkles size={14} /> Generate Demo Data
              </Button>
            )}
            {enablePortalFormSubmission && !readOnly && (
              <Button variant="primary" onClick={handleCreateSubmission}>
                <Plus size={14} /> Create Submission
              </Button>
            )}
          </HeroActions>
        </PageHero>

        {/* Controls Bar */}
        <ControlsBar>
          <ControlsLeft>
            <Button variant="secondary" onClick={() => setFilterPanelOpen(true)}>
              <Filter size={14} /> Filters
              {fieldFilters.length > 0 && (
                <span style={{ marginLeft: '4px', background: theme.colors.tint, color: '#fff', borderRadius: '10px', padding: '1px 7px', fontSize: '11px', fontWeight: 700 }}>
                  {fieldFilters.length}
                </span>
              )}
            </Button>
            <Button variant={analyticsPanelOpen ? 'primary' : 'secondary'} onClick={() => setAnalyticsPanelOpen(prev => !prev)}>
              <BarChart3 size={14} /> Analytics
            </Button>
            <ControlsDivider />
            <Button
              variant={compactView ? 'primary' : 'secondary'}
              onClick={() => { const next = !compactView; setCompactView(next); try { localStorage.setItem('aqrate_compact_view', String(next)); } catch {} }}
            >
              {compactView ? <Rows4 size={14} /> : <Rows3 size={14} />}
              {compactView ? 'Compact' : 'Comfortable'}
            </Button>
            <ControlsDivider />
            <Button variant="secondary" onClick={() => setColumnPanelOpen(true)} disabled={configLoading}>
              <Settings size={14} /> Columns
              {currentConfig && <span style={{ fontSize: '12px', color: theme.colors.muted, marginLeft: '4px' }}>({currentConfig.name})</span>}
              {configSaving && <span style={{ marginLeft: '4px', display: 'inline-flex' }}><Save size={14} /></span>}
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/forms/${formId}/pdf-templates`)}>
              <FileText size={14} /> Report Templates
            </Button>
            <Button variant="secondary" onClick={() => navigate(`/forms/${formId}/visualizations`)}>
              <BarChart3 size={14} /> Visualizations
            </Button>
            <StandaloneVizListLauncher formSchemaId={parseInt(formId, 10)} filterOverride={listVizFilterOverride} />
            <ControlsDivider />
            <ShowDeletedToggle theme={theme} onClick={() => setShowInactive(!showInactive)}>
              <ToggleTrack $on={showInactive}>
                <ToggleThumb $on={showInactive} />
              </ToggleTrack>
              Show Deleted
            </ShowDeletedToggle>
          </ControlsLeft>
          <ControlsRight>
            <Button
              variant="secondary"
              onClick={() => setEditFormWarningOpen(true)}
              title="Edit Form"
              style={{ padding: '6px 10px', minWidth: 'auto' }}
            >
              <Pencil size={15} />
            </Button>
          </ControlsRight>
        </ControlsBar>

        <AnalyticsPanel
          isOpen={analyticsPanelOpen}
          aggregationData={aggregationData}
          isLoading={aggregationLoading}
          schemaFields={formData?.schemaDefinition?.fields || []}
          formSchemaId={formId}
          fieldFilters={fieldFilters}
          filterLogic={filterLogic}
          showInactive={showInactive}
        />

        {fieldFilters.length > 0 && (
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: '6px',
            alignItems: 'center', marginBottom: theme.spacing.md,
            padding: `${theme.spacing.sm} ${theme.spacing.md}`,
            background: theme.colors.backgroundSecondary,
            border: `1px solid ${theme.colors.border}`,
            borderRadius: theme.borderRadius.lg
          }}>
            <span style={{ fontSize: '12px', color: theme.colors.muted, fontWeight: 600, marginRight: '4px' }}>
              {filterLogic === 'or' ? 'Matching ANY:' : 'Matching ALL:'}
            </span>
            {fieldFilters.map((f, i) => (
              <React.Fragment key={i}>
                {i > 0 && (
                  <span style={{ fontSize: '11px', fontWeight: 700, color: theme.colors.muted, padding: '0 2px' }}>
                    {filterLogic === 'or' ? 'OR' : 'AND'}
                  </span>
                )}
                <span style={{
                  display: 'inline-flex', alignItems: 'center', gap: '4px',
                  padding: '3px 10px', fontSize: '13px',
                  background: theme.colors.tint + '12', color: theme.colors.tint,
                  border: `1px solid ${theme.colors.tint}25`,
                  borderRadius: theme.borderRadius.round
                }}>
                  <strong style={{ fontWeight: 600 }}>{getFieldTitle(f.fieldKey)}</strong>
                  <span style={{ opacity: 0.8 }}>{formatFilterLabel(f)}</span>
                  <button
                    onClick={() => removeFieldFilter(i)}
                    style={{
                      background: 'none', border: 'none', cursor: 'pointer',
                      color: theme.colors.tint, padding: '0 0 0 2px', lineHeight: 1,
                      display: 'inline-flex', alignItems: 'center'
                    }}
                  >
                    <X size={12} />
                  </button>
                </span>
              </React.Fragment>
            ))}
            <button
              onClick={clearAllFieldFilters}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: theme.colors.muted, fontSize: '12px', textDecoration: 'underline', marginLeft: '4px'
              }}
            >
              Clear all
            </button>
          </div>
        )}

        <FilterPanel
          isOpen={filterPanelOpen}
          onClose={() => setFilterPanelOpen(false)}
          schemaFields={formData?.schemaDefinition?.fields || []}
          filters={fieldFilters}
          filterLogic={filterLogic}
          onFiltersChange={handleFieldFiltersChange}
          onFilterLogicChange={setFilterLogic}
        />

        {!loading && totalCount > 0 && (
          <p style={{ margin: '0 0 10px 4px', fontSize: 13, fontWeight: 500, color: '#43474f' }}>
            {totalCount.toLocaleString()} submission{totalCount !== 1 ? 's' : ''}{showInactive ? ' (including deleted)' : ''}
          </p>
        )}

        {submissions.length === 0 && !loading ? (
          <EmptyState theme={theme}>
            <EmptyIcon>{fieldFilters.length > 0 ? <Filter size={48} /> : <Inbox size={48} />}</EmptyIcon>
            <ThemedText type="subtitle" style={{ marginBottom: theme.spacing.md }}>
              {fieldFilters.length > 0 ? 'No matching submissions' : 'No submissions yet'}
            </ThemedText>
            <ThemedText style={{ color: theme.colors.muted, marginBottom: theme.spacing.md }}>
              {fieldFilters.length > 0
                ? 'No submissions match the current filters. Try adjusting your filter criteria.'
                : 'Submissions will appear here once users start filling out this form'}
            </ThemedText>
            {fieldFilters.length > 0 && (
              <Button variant="secondary" onClick={clearAllFieldFilters}>
                <RotateCcw size={14} /> Clear Filters
              </Button>
            )}
          </EmptyState>
        ) : (
          <>
          {hasHorizontalScroll && (
            <TopScrollBar ref={topScrollRef} onScroll={handleTopScroll}>
              <div style={{ width: compactView ? '100%' : table.getTotalSize(), height: 1 }} />
            </TopScrollBar>
          )}
          <SubmissionsTable theme={theme} $compact={compactView} $noTopBar={!hasHorizontalScroll} ref={tableScrollRef} onScroll={handleTableScroll}>
            <TableHeader theme={theme} width={compactView ? undefined : table.getTotalSize()} $compact={compactView}>
              {table.getHeaderGroups().map(headerGroup => (
                <React.Fragment key={headerGroup.id}>
                  {headerGroup.headers.map(header => {
                    const isShrink = ['select', 'index', 'actions'].includes(header.id);
                    const isNarrow = ['select', 'index'].includes(header.id);
                    return (
                      <HeaderCell
                        key={header.id}
                        theme={theme}
                        $compact={compactView}
                        $narrow={isNarrow}
                        style={compactView
                          ? (isShrink ? { flexShrink: 0, width: 'auto' } : { flex: 1, minWidth: 0 })
                          : { width: header.getSize() }
                        }
                        stickyLeft={!compactView && ['select', 'index'].includes(header.id)}
                        stickyOffset={header.id === 'index' ? 30 : 0}
                        stickyRight={!compactView && header.id === 'actions'}
                      >
                        {header.isPlaceholder ? null : flexRender(header.column.columnDef.header, header.getContext())}
                        {!compactView && header.column.getCanResize() && (
                          <Resizer
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                            className={header.column.getIsResizing() ? 'isResizing' : ''}
                          />
                        )}
                      </HeaderCell>
                    );
                  })}
                </React.Fragment>
              ))}
            </TableHeader>

            {table.getRowModel().rows.map(row => {
              const unreadCount = unreadCommentCounts[row.original.id] || 0;
              return (
              <TableRow
                key={row.id}
                theme={theme}
                $deleted={row.original._isDeleted}
                width={compactView ? undefined : table.getTotalSize()}
                onClick={() => handleSubmissionClick(row.original)}
              >
                {row.getVisibleCells().map(cell => {
                  const isShrink = ['select', 'index', 'actions'].includes(cell.column.id);
                  const isNarrow = ['select', 'index'].includes(cell.column.id);
                  return (
                    <TableCell
                      key={cell.id}
                      theme={theme}
                      $compact={compactView}
                      $narrow={isNarrow}
                      style={compactView
                        ? (isShrink ? { flexShrink: 0, width: 'auto' } : { flex: 1, minWidth: 0 })
                        : { width: cell.column.getSize() }
                      }
                      stickyLeft={!compactView && ['select', 'index'].includes(cell.column.id)}
                      stickyOffset={cell.column.id === 'index' ? 30 : 0}
                      stickyRight={!compactView && cell.column.id === 'actions'}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      {cell.column.id === 'index' && unreadCount > 0 && (
                        <span style={{
                          marginLeft: '6px',
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          minWidth: '18px',
                          height: '18px',
                          padding: '0 5px',
                          borderRadius: '9px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          fontSize: '10px',
                          fontWeight: 700,
                          lineHeight: 1,
                        }} title={`${unreadCount} unread comment${unreadCount !== 1 ? 's' : ''}`}>
                          {unreadCount}
                        </span>
                      )}
                      {cell.column.id === 'submitter' && row.original._isDeleted && (
                        <span style={{ marginLeft: '8px', padding: '2px 6px', borderRadius: '4px', backgroundColor: theme.colors.muted, color: 'white', fontSize: '10px', fontWeight: 600, textTransform: 'uppercase' }}>
                          DELETED
                        </span>
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            );
            })}
          </SubmissionsTable>
          <TableFooter>
            <FooterInfo>
              {loading ? 'Loading…' : `Showing ${startIndex}–${endIndex} of ${totalCount} submission${totalCount !== 1 ? 's' : ''}`}
            </FooterInfo>
            <FooterNav>
              <FooterNavBtn onClick={() => goToPage(currentPage - 1)} disabled={currentPage <= 1}>
                ← Previous
              </FooterNavBtn>
              <FooterNavBtn onClick={() => goToPage(currentPage + 1)} disabled={currentPage >= totalPages}>
                Next →
              </FooterNavBtn>
            </FooterNav>
          </TableFooter>
          </>
        )
        }

        {/* Edit Form Warning Modal */}
        <Modal theme={theme} isOpen={editFormWarningOpen}>
          <ModalContent theme={theme} style={{ maxWidth: 480 }}>
            <ModalHeader theme={theme}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  background: '#fef3c7', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0
                }}>
                  <Pencil size={20} color="#b45309" />
                </div>
                <div>
                  <ThemedText type="subtitle" style={{ margin: 0 }}>Edit Form Structure</ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted, margin: 0 }}>Review before making changes</ThemedText>
                </div>
              </div>
              <Button variant="secondary" size="sm" onClick={() => setEditFormWarningOpen(false)} style={{ padding: '4px 8px', minWidth: 'auto' }}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <div style={{ padding: '0 0 24px 0' }}>
              <div style={{
                background: '#fffbeb',
                border: '1px solid #fcd34d',
                borderLeft: '4px solid #f59e0b',
                borderRadius: 10,
                padding: '14px 16px',
                marginBottom: 16,
              }}>
                <p style={{ margin: '0 0 10px 0', fontWeight: 600, fontSize: 13, color: '#92400e' }}>
                  Editing this form may affect how existing submissions are displayed
                </p>
                <p style={{ margin: 0, fontSize: 13, color: '#78350f', lineHeight: 1.65 }}>
                  If you remove or rename a field, previously collected data for that field will still be stored but may not be visible when viewing existing submissions. New submissions will use the updated form structure.
                </p>
              </div>
              <p style={{ margin: '0 0 20px 0', fontSize: 13, color: theme.colors.muted, lineHeight: 1.65 }}>
                You can safely reorder fields, change labels, or add new ones without affecting existing submission data. If you need to restore a removed field, re-add it with the same field key to make historical data visible again.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <Button variant="secondary" onClick={() => setEditFormWarningOpen(false)}>
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={() => { setEditFormWarningOpen(false); navigate(`/form-builder/${formId}`, { state: { skipWarning: true } }); }}
                >
                  <Pencil size={14} /> I understand, continue editing
                </Button>
              </div>
            </div>
          </ModalContent>
        </Modal>

        {/* Location Modal */}
        <Modal theme={theme} isOpen={locationModalOpen}>
          <ModalContent theme={theme}>
            <ModalHeader theme={theme}>
              <div>
                <ThemedText type="subtitle">Submission Location</ThemedText>
                {selectedLocation && (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    {selectedLocation.submitter}
                  </ThemedText>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => setLocationModalOpen(false)}
              >
                <X size={14} />
              </Button>
            </ModalHeader>

            {selectedLocation && (
              <>
                <div style={{ marginBottom: theme.spacing.md }}>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Coordinates: {selectedLocation.latitude.toFixed(6)}, {selectedLocation.longitude.toFixed(6)}
                  </ThemedText>
                </div>

                <div style={{ marginBottom: theme.spacing.md }}>
                  <MiniMap
                    latitude={selectedLocation.latitude}
                    longitude={selectedLocation.longitude}
                    height="400px"
                    zoom={15}
                    label={selectedLocation.name}
                    showButtons={false}
                  />
                </div>

                <div style={{ marginTop: theme.spacing.md }}>
                  <MapLinksDropdown
                    latitude={selectedLocation.latitude}
                    longitude={selectedLocation.longitude}
                    label="Open in Maps"
                    openUpward={true}
                  />
                </div>
              </>
            )}
          </ModalContent>
        </Modal>

        {/* Field Media Modal */}
        <Modal theme={theme} isOpen={fieldMediaModal.open}>
          <ModalContent theme={theme} style={{ maxWidth: fieldMediaModal.type === 'audio' ? 500 : 900 }}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">
                {fieldMediaModal.type === 'location' ? 'Location' : fieldMediaModal.type === 'audio' ? 'Voice Recording' : 'Images'}
              </ThemedText>
              <Button variant="secondary" size="sm" onClick={() => setFieldMediaModal({ open: false, type: null, data: null, loading: false })}>
                <X size={14} />
              </Button>
            </ModalHeader>

            {fieldMediaModal.loading && (
              <div style={{ textAlign: 'center', padding: theme.spacing.xl }}>
                <Loader size={24} style={{ animation: 'spin 1s linear infinite' }} />
                <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.sm }}>Loading...</ThemedText>
              </div>
            )}

            {/* Location */}
            {fieldMediaModal.type === 'location' && fieldMediaModal.data && (
              <>
                <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.md }}>
                  {fieldMediaModal.data.latitude.toFixed(6)}, {fieldMediaModal.data.longitude.toFixed(6)}
                  {fieldMediaModal.data.accuracy && ` (±${Math.round(fieldMediaModal.data.accuracy)}m)`}
                </ThemedText>
                <MiniMap
                  latitude={fieldMediaModal.data.latitude}
                  longitude={fieldMediaModal.data.longitude}
                  height="400px"
                  zoom={15}
                  label={fieldMediaModal.data.label}
                  accuracy={fieldMediaModal.data.accuracy}
                  showButtons={false}
                />
                <div style={{ marginTop: theme.spacing.md }}>
                  <MapLinksDropdown
                    latitude={fieldMediaModal.data.latitude}
                    longitude={fieldMediaModal.data.longitude}
                    label="Open in Maps"
                    openUpward={true}
                  />
                </div>
              </>
            )}

            {/* Images */}
            {fieldMediaModal.type === 'image' && fieldMediaModal.data && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: theme.spacing.md }}>
                {fieldMediaModal.data.attachments.length === 0 ? (
                  <ThemedText style={{ color: theme.colors.muted }}>No images found</ThemedText>
                ) : fieldMediaModal.data.attachments.map(att => (
                  <a key={att.id} href={att.fileUrl} target="_blank" rel="noopener noreferrer">
                    <img
                      src={att.fileUrl}
                      alt={att.fileName}
                      style={{
                        maxWidth: 400,
                        maxHeight: 400,
                        objectFit: 'contain',
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${theme.colors.border}`,
                      }}
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="200" height="200"%3E%3Crect fill="%23ddd" width="200" height="200"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EUnavailable%3C/text%3E%3C/svg%3E';
                      }}
                    />
                    <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: 4, display: 'block' }}>{att.fileName}</ThemedText>
                  </a>
                ))}
              </div>
            )}

            {/* Audio */}
            {fieldMediaModal.type === 'audio' && fieldMediaModal.data && (
              <div>
                {fieldMediaModal.data.attachments.length === 0 ? (
                  <ThemedText style={{ color: theme.colors.muted }}>No audio found</ThemedText>
                ) : fieldMediaModal.data.attachments.map(att => (
                  <div key={att.id} style={{ marginBottom: theme.spacing.md }}>
                    <WaveformAudioPlayer src={att.fileUrl} theme={theme} attachment={att} />
                    {att.transcript && (
                      <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.sm, fontStyle: 'italic' }}>
                        {att.transcript}
                      </ThemedText>
                    )}
                    <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: 4 }}>{att.fileName}</ThemedText>
                  </div>
                ))}
              </div>
            )}
          </ModalContent>
        </Modal>

        {/* Work Order Detail Modal */}
        <Modal theme={theme} isOpen={workOrderModalOpen}>
          <ModalContent theme={theme} style={{ maxWidth: '600px' }}>
            <ModalHeader theme={theme}>
              <div>
                <ThemedText type="subtitle">Work Order Details</ThemedText>
                {selectedWorkOrder && (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    {selectedWorkOrder.title || `WO-${selectedWorkOrder.id}`}
                  </ThemedText>
                )}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setWorkOrderModalOpen(false); setSelectedWorkOrder(null); }}
              >
                <X size={14} />
              </Button>
            </ModalHeader>

            {workOrderLoading && !selectedWorkOrder && (
              <div style={{ textAlign: 'center', padding: theme.spacing.xl }}>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>Loading work order details...</ThemedText>
              </div>
            )}

            {selectedWorkOrder && (
              <>
                <div style={{ display: 'flex', gap: theme.spacing.sm, marginBottom: theme.spacing.lg, flexWrap: 'wrap', alignItems: 'center' }}>
                  {selectedWorkOrder.status && (
                    <WOStatusBadge theme={theme} status={selectedWorkOrder.status}>
                      {getWorkOrderStatusLabel(selectedWorkOrder.status)}
                    </WOStatusBadge>
                  )}
                  {(selectedWorkOrder.priority !== undefined && selectedWorkOrder.priority !== null) && (
                    <WOPriorityBadge theme={theme} priority={selectedWorkOrder.priority}>
                      {getWOPriorityIcon(selectedWorkOrder.priority)} {WO_PRIORITY_LABELS[selectedWorkOrder.priority] || 'Medium'}
                    </WOPriorityBadge>
                  )}
                </div>

                <WOModalBody theme={theme}>
                  <WOModalField>
                    <WOModalFieldLabel theme={theme}>Customer</WOModalFieldLabel>
                    <ThemedText type="small">
                      {selectedWorkOrder.customerName || '-'}
                    </ThemedText>
                  </WOModalField>
                  <WOModalField>
                    <WOModalFieldLabel theme={theme}>Assigned To</WOModalFieldLabel>
                    <ThemedText type="small">
                      {selectedWorkOrder.assignedToName || '-'}
                    </ThemedText>
                  </WOModalField>
                  <WOModalField>
                    <WOModalFieldLabel theme={theme}>Due Date</WOModalFieldLabel>
                    <ThemedText type="small">
                      {selectedWorkOrder.dueDate ? formatDateOnly(selectedWorkOrder.dueDate) : '-'}
                    </ThemedText>
                  </WOModalField>
                  <WOModalField>
                    <WOModalFieldLabel theme={theme}>Service Date</WOModalFieldLabel>
                    <ThemedText type="small">
                      {selectedWorkOrder.serviceDate ? formatDateOnly(selectedWorkOrder.serviceDate) : '-'}
                    </ThemedText>
                  </WOModalField>
                  <WOModalField>
                    <WOModalFieldLabel theme={theme}>Location</WOModalFieldLabel>
                    <ThemedText type="small">
                      {selectedWorkOrder.locationName || '-'}
                    </ThemedText>
                  </WOModalField>
                  <WOModalField>
                    <WOModalFieldLabel theme={theme}>WO Number</WOModalFieldLabel>
                    <ThemedText type="small">
                      {selectedWorkOrder.workOrderNumber || `WO-${selectedWorkOrder.id}`}
                    </ThemedText>
                  </WOModalField>
                </WOModalBody>

                {selectedWorkOrder.description && (
                  <div style={{ marginBottom: theme.spacing.lg }}>
                    <WOModalFieldLabel theme={theme} style={{ marginBottom: '4px' }}>Description</WOModalFieldLabel>
                    <ThemedText type="small" style={{ color: theme.colors.muted, whiteSpace: 'pre-wrap' }}>
                      {selectedWorkOrder.description}
                    </ThemedText>
                  </div>
                )}

                {selectedWorkOrder.notes && (
                  <div style={{ marginBottom: theme.spacing.lg }}>
                    <WOModalFieldLabel theme={theme} style={{ marginBottom: '4px' }}>Notes</WOModalFieldLabel>
                    <ThemedText type="small" style={{ color: theme.colors.muted, whiteSpace: 'pre-wrap' }}>
                      {selectedWorkOrder.notes}
                    </ThemedText>
                  </div>
                )}

                <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: theme.spacing.md }}>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      setWorkOrderModalOpen(false);
                      setSelectedWorkOrder(null);
                      navigate(`/work-orders/${selectedWorkOrder.id}`);
                    }}
                  >
                    View Full Details
                  </Button>
                </div>
              </>
            )}
          </ModalContent>
        </Modal>

        {/* Column Management Modal */}
        <Modal isOpen={columnPanelOpen} theme={theme}>
          <ModalContent style={{ maxWidth: '1100px', height: '80vh', display: 'flex', flexDirection: 'column', padding: 0 }} theme={theme}>
            <ModalHeader theme={theme} style={{ borderBottom: `1px solid ${theme.colors.border}`, padding: theme.spacing.md }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md, flex: 1 }}>
                <ThemedText type="subtitle">Manage Columns</ThemedText>
                {currentConfig && (
                  <>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      Configuration:
                    </ThemedText>
                    <ThemedText type="defaultSemiBold" style={{ color: theme.colors.tint }}>
                      {currentConfig.name}
                    </ThemedText>
                    {configSaving && (
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        <Save size={14} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '4px' }} /> Saving...
                      </ThemedText>
                    )}
                  </>
                )}
                {!currentConfig && (
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Using default layout (not saved)
                  </ThemedText>
                )}
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                <ColumnSelector theme={theme}>
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => setShowConfigSelector(!showConfigSelector)}
                  >
                    <ClipboardList size={14} /> Configurations
                  </Button>

                  {showConfigSelector && (
                    <ConfigSelectorDropdown theme={theme} onClick={(e) => e.stopPropagation()}>
                      <div style={{ padding: theme.spacing.sm, borderBottom: `1px solid ${theme.colors.border}` }}>
                        <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.xs }}>
                          Column Configurations
                        </ThemedText>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          Select or manage your column layouts
                        </ThemedText>
                      </div>

                      <ConfigSection theme={theme}>
                        <ConfigSectionTitle theme={theme}>My Configurations</ConfigSectionTitle>
                        {(() => {
                          const myConfigs = availableConfigs.filter(c => c.isOwner);
                          console.log('[ColumnConfig Modal] Available configs:', availableConfigs);
                          console.log('[ColumnConfig Modal] My configs (isOwner=true):', myConfigs);
                          return myConfigs;
                        })()
                          .map(config => (
                            <ConfigOption
                              key={config.id}
                              theme={theme}
                              isActive={currentConfig?.id === config.id}
                              onClick={() => handleSwitchConfig(config.id)}
                            >
                              {editingConfigName === config.id ? (
                                <NewConfigInput
                                  theme={theme}
                                  defaultValue={config.name}
                                  autoFocus
                                  onBlur={(e) => {
                                    if (e.target.value.trim() !== config.name) {
                                      handleRenameConfig(config.id, e.target.value);
                                    } else {
                                      setEditingConfigName(null);
                                    }
                                  }}
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') e.target.blur();
                                    else if (e.key === 'Escape') setEditingConfigName(null);
                                  }}
                                  onClick={(e) => e.stopPropagation()}
                                />
                              ) : (
                                <>
                                  <ThemedText type="default" style={{ flex: 1 }}>{config.name}</ThemedText>
                                  {config.isDefault && <ConfigBadge theme={theme} type="default">Default</ConfigBadge>}
                                  {config.isShared && <ConfigBadge theme={theme} type="shared">Shared</ConfigBadge>}
                                  <ConfigActions theme={theme} onClick={(e) => e.stopPropagation()}>
                                    {!config.isDefault && (
                                      <IconButton theme={theme} onClick={() => handleSetDefault(config.id)} title="Set as default"><Star size={14} /></IconButton>
                                    )}
                                    <IconButton theme={theme} onClick={() => handleToggleShare(config.id)} title={config.isShared ? 'Stop sharing' : 'Share with team'}>{config.isShared ? <Unlock size={14} /> : <Lock size={14} />}</IconButton>
                                    <IconButton theme={theme} onClick={() => setEditingConfigName(config.id)} title="Rename"><Pencil size={14} /></IconButton>
                                    <IconButton theme={theme} onClick={() => handleDuplicateConfig(config.id, config.name)} title="Duplicate"><Copy size={14} /></IconButton>
                                    <IconButton theme={theme} onClick={() => handleDeleteConfig(config.id)} title="Delete"><Trash2 size={14} /></IconButton>
                                  </ConfigActions>
                                </>
                              )}
                            </ConfigOption>
                          ))}

                        {availableConfigs.filter(c => c.isOwner).length < columnConfigurationService.MAX_CONFIGS_PER_USER && (
                          <ConfigOption
                            theme={theme}
                            onClick={() => {
                              setShowSaveNameModal(true);
                              setShowConfigSelector(false);
                            }}
                            style={{ borderTop: `1px dashed ${theme.colors.border}`, marginTop: theme.spacing.xs }}
                          >
                            <Plus size={16} />
                            <ThemedText type="default">Create New Configuration</ThemedText>
                          </ConfigOption>
                        )}
                      </ConfigSection>

                      {availableConfigs.filter(c => c.isShared && !c.isOwner).length > 0 && (
                        <ConfigSection theme={theme}>
                          <ConfigSectionTitle theme={theme}>Shared by Team</ConfigSectionTitle>
                          {availableConfigs
                            .filter(c => c.isShared && !c.isOwner)
                            .map(config => (
                              <ConfigOption
                                key={config.id}
                                theme={theme}
                                isActive={currentConfig?.id === config.id}
                                onClick={() => handleSwitchConfig(config.id)}
                              >
                                <ThemedText type="default" style={{ flex: 1 }}>{config.name}</ThemedText>
                                <ThemedText type="small" style={{ color: theme.colors.muted }}>by {config.ownerName}</ThemedText>
                                <ConfigActions theme={theme} onClick={(e) => e.stopPropagation()}>
                                  <IconButton theme={theme} onClick={() => handleSetDefault(config.id)} title="Use as my default (creates copy)"><Star size={14} /></IconButton>
                                  <IconButton theme={theme} onClick={() => handleDuplicateConfig(config.id, config.name)} title="Duplicate to customize"><Copy size={14} /></IconButton>
                                </ConfigActions>
                              </ConfigOption>
                            ))}
                        </ConfigSection>
                      )}

                      <div style={{ padding: theme.spacing.sm, borderTop: `1px solid ${theme.colors.border}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <ThemedText type="small" style={{ color: theme.colors.muted }}>
                          {availableConfigs.filter(c => c.isOwner).length} / {columnConfigurationService.MAX_CONFIGS_PER_USER} configs
                        </ThemedText>
                        <Button variant="text" size="sm" onClick={() => setShowConfigSelector(false)}>Close</Button>
                      </div>
                    </ConfigSelectorDropdown>
                  )}
                </ColumnSelector>

                {!currentConfig && (
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => {
                      const userConfigs = availableConfigs.filter(c => c.isOwner);
                      if (userConfigs.length === 0) {
                        handleCreateDefaultConfig();
                      } else {
                        setShowSaveNameModal(true);
                      }
                    }}
                  >
                    <Save size={14} /> Save Layout
                  </Button>
                )}
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => {
                    setConfirmState({
                      isOpen: true,
                      title: 'Reset Columns',
                      message: 'Reset columns to default?',
                      confirmLabel: 'Reset',
                      onConfirm: () => {
                        closeConfirm();
                        const defaults = BASE_COLUMNS.filter(col => col.default).map(col => col.key);
                        updateVisibleColumns(defaults);
                        updateColumnSizing({});
                      },
                    });
                  }}
                >
                  Reset to Default
                </Button>
                <Button variant="primary" size="sm" onClick={() => setColumnPanelOpen(false)}>
                  Done
                </Button>
                <Button variant="text" onClick={() => setColumnPanelOpen(false)}><X size={14} /></Button>
              </div>
            </ModalHeader>

            <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
              {/* Left Column: Visible */}
              <div style={{ flex: 1, padding: theme.spacing.lg, overflowY: 'auto', borderRight: `1px solid ${theme.colors.border}`, display: 'flex', flexDirection: 'column' }}>
                <div style={{ marginBottom: theme.spacing.md, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <ThemedText type="defaultSemiBold">Visible Columns</ThemedText>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>Drag to reorder</ThemedText>
                </div>

                {/* Frozen Hint */}
                <div style={{ marginBottom: theme.spacing.md, padding: theme.spacing.sm, backgroundColor: theme.colors.background, borderRadius: theme.borderRadius.md, border: `1px dashed ${theme.colors.border}`, display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
                  <Lock size={16} />
                  <ThemedText type="small" style={{ color: theme.colors.muted, flex: 1 }}>
                    Fixed Columns: <strong>{frozenColumns.map(k => availableColumns.find(c => c.key === k)?.label).join(', ')}</strong>
                  </ThemedText>
                </div>

                {/* Draggable List */}
                <div style={{ flex: 1 }}>
                  <DraggableColumnList
                    availableColumns={availableColumns}
                    frozenColumns={frozenColumns}
                    initialReorderableColumns={visibleColumns.filter(c => !frozenColumns.includes(c))}
                    onReorder={handleColumnReorder}
                    toggleColumn={toggleColumn}
                    theme={theme}
                  />
                </div>
              </div>

              {/* Right Column: Available Fields */}
              <div style={{ flex: 1, padding: theme.spacing.lg, overflowY: 'auto', backgroundColor: theme.colors.background }}>
                <ThemedText type="defaultSemiBold" style={{ marginBottom: theme.spacing.md }}>
                  Available Fields
                </ThemedText>

                {(() => {
                  const toggleableColumns = availableColumns
                    .filter(c => !frozenColumns.includes(c.key));

                  if (toggleableColumns.length === 0) {
                    return (
                      <div style={{ padding: theme.spacing.xl, textAlign: 'center', opacity: 0.5 }}>
                        <ThemedText>No fields available.</ThemedText>
                      </div>
                    );
                  }

                  // Group ALL toggleable columns by category
                  const groups = {};
                  toggleableColumns.forEach(col => {
                    const category = getColumnCategory(col);
                    if (!groups[category]) groups[category] = [];
                    groups[category].push(col);
                  });

                  const categoryOrder = ['System', 'Work Order', 'Form Fields', '🧪 Lab Results'];
                  return categoryOrder
                    .filter(cat => groups[cat]?.length > 0)
                    .map(category => {
                      const catColumns = groups[category];
                      const visibleInCat = catColumns.filter(c => visibleColumns.includes(c.key)).length;
                      const allVisible = visibleInCat === catColumns.length;
                      const noneVisible = visibleInCat === 0;

                      return (
                        <div key={category} style={{ marginBottom: theme.spacing.lg }}>
                          {/* Category header */}
                          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: theme.spacing.sm, paddingBottom: theme.spacing.xs, borderBottom: `1px solid ${theme.colors.border}` }}>
                            <ThemedText type="small" style={{
                              fontWeight: 600, color: theme.colors.text, fontSize: '13px',
                            }}>
                              {category}
                              <span style={{ color: theme.colors.muted, fontWeight: 400, marginLeft: '6px' }}>
                                {visibleInCat}/{catColumns.length}
                              </span>
                            </ThemedText>
                            <div style={{ display: 'flex', gap: theme.spacing.sm }}>
                              <button
                                onClick={() => addColumnsByCategory(category)}
                                disabled={allVisible}
                                style={{
                                  background: 'none', border: 'none', cursor: allVisible ? 'default' : 'pointer',
                                  color: allVisible ? theme.colors.muted + '60' : theme.colors.tint,
                                  fontSize: '12px', fontWeight: 600, padding: '2px 4px', display: 'flex', alignItems: 'center', gap: '3px'
                                }}
                              >
                                <PlusCircle size={12} /> Add All
                              </button>
                              <button
                                onClick={() => removeColumnsByCategory(category)}
                                disabled={noneVisible}
                                style={{
                                  background: 'none', border: 'none', cursor: noneVisible ? 'default' : 'pointer',
                                  color: noneVisible ? theme.colors.muted + '60' : theme.colors.muted,
                                  fontSize: '12px', fontWeight: 600, padding: '2px 4px', display: 'flex', alignItems: 'center', gap: '3px'
                                }}
                              >
                                <MinusCircle size={12} /> Remove All
                              </button>
                            </div>
                          </div>

                          {/* Field list */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            {catColumns.map(column => {
                              const isVisible = visibleColumns.includes(column.key);
                              return (
                                <div
                                  key={column.key}
                                  onClick={() => toggleColumn(column.key)}
                                  style={{
                                    display: 'flex', alignItems: 'center', gap: theme.spacing.sm,
                                    padding: '6px 10px', borderRadius: theme.borderRadius.md,
                                    cursor: 'pointer', transition: 'background 0.15s',
                                    backgroundColor: isVisible ? theme.colors.tint + '08' : 'transparent',
                                  }}
                                  onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = isVisible ? theme.colors.tint + '15' : theme.colors.hover; }}
                                  onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = isVisible ? theme.colors.tint + '08' : 'transparent'; }}
                                >
                                  <Checkbox
                                    theme={theme}
                                    checked={isVisible}
                                    onChange={() => toggleColumn(column.key)}
                                    onClick={(e) => e.stopPropagation()}
                                  />
                                  <div style={{ color: theme.colors.muted, display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                                    {renderFieldTypeIcon(column.fieldType, 14)}
                                  </div>
                                  <span style={{ flex: 1, fontSize: '13px', fontWeight: 500, color: theme.colors.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {column.label}
                                  </span>
                                  {column.fieldType && (
                                    <span style={{
                                      fontSize: '10px', padding: '1px 6px', borderRadius: '3px',
                                      background: theme.colors.hover || '#f0f0f0', color: theme.colors.muted,
                                      textTransform: 'uppercase', fontWeight: 500, flexShrink: 0
                                    }}>
                                      {column.fieldType}
                                    </span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                })()}
              </div>
            </div>
          </ModalContent>
        </Modal>

        {/* Save Configuration Name Modal */}
        <Modal isOpen={showSaveNameModal} theme={theme}>
          <ModalContent theme={theme} style={{ maxWidth: '400px', padding: theme.spacing.xl }}>
            <ModalHeader theme={theme} style={{ marginBottom: theme.spacing.lg }}>
              <ThemedText type="subtitle">Save Configuration</ThemedText>
              <Button variant="text" onClick={() => setShowSaveNameModal(false)}>
                <X size={14} />
              </Button>
            </ModalHeader>
            <form onSubmit={(e) => {
              e.preventDefault();
              const name = e.target.elements.configName.value.trim();
              if (name) {
                handleCreateConfig(name);
                setShowSaveNameModal(false);
              }
            }}>
              <div style={{ marginBottom: theme.spacing.lg }}>
                <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.sm, display: 'block' }}>
                  Configuration Name
                </ThemedText>
                <NewConfigInput
                  theme={theme}
                  name="configName"
                  placeholder="e.g., Lab Results View"
                  autoFocus
                  style={{ width: '100%' }}
                />
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
                <Button variant="secondary" size="sm" type="button"
                  onClick={() => setShowSaveNameModal(false)}>
                  Cancel
                </Button>
                <Button variant="primary" size="sm" type="submit">
                  <Save size={14} /> Save
                </Button>
              </div>
            </form>
          </ModalContent>
        </Modal>

        <ConfirmDialog
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          cancelLabel={confirmState.cancelLabel}
          onConfirm={confirmState.onConfirm}
          onCancel={confirmState.onCancel || closeConfirm}
        />
        {canGenerateDemoData && (
          <GenerateDemoDataDialog
            formSchemaId={formId}
            formName={formData?.name}
            open={isDemoDataOpen}
            onClose={() => setIsDemoDataOpen(false)}
            onCommitted={() => { setIsDemoDataOpen(false); window.location.reload(); }}
          />
        )}
      </PageContainer >
    </Layout >
  );
};
